/**
 * Advanced AI System Form Component
 * Features:
 * - Custom expert nodes with ports and detailed visualization
 * - Side panel for expert configuration
 * - Auto-layout with dagre algorithm
 * - Real-time workflow validation
 * - Execution flow visualization
 * - Advanced canvas controls
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Box,
  Tabs,
  Tab,
  Typography,
  Alert,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemButton,
  IconButton,
  Chip,
  Divider,
  Badge,
  Stack,
  Card,
  CardContent,
  Grid,
  AlertTitle,
  ButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Slider,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Tooltip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  Add as AddIcon,
  AccountTree as AutoLayoutIcon,
  CheckCircle as ValidIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  FitScreen as FitScreenIcon,
  Search as SearchIcon,
  Info as InfoIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  RemoveCircleOutline as RemoveCanvasIcon,
  PlayArrow as PlayArrowIcon,
  Schedule as QueuedIcon,
  Stop as StopIcon,
  Visibility as ViewIcon,
  ShowChart as ShowChartIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import {
  ReactFlow,
  MiniMap,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  Node,
  Edge,
  Connection,
  BackgroundVariant,
  Panel,
  NodeProps,
  Handle,
  Position,
  MarkerType,
  useReactFlow,
  ReactFlowProvider,
  EdgeProps,
  BaseEdge,
  EdgeLabelRenderer,
  getSmoothStepPath,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { toast } from 'react-toastify';
import dagre from 'dagre';

import { aiSystemApi, expertApi, executionApi, agentApi } from '../../services/api';
import type { AISystem, AISystemCreate, AISystemValidationReport, ExpertListItem, Expert, ExpertType, AISystemExecution, AISystemExecutionListItem, ExpertCreate } from '../../types';
import { normalizeTags } from '../../utils/templateSearch';
import { ExpertForm } from '../expert/ExpertForm';
import { ExpertEditForm } from '../expert/ExpertEditForm';
import { PromptEditor, InputDefinition } from '../common/PromptEditor';
import { AgentAutocomplete } from '../common/AsyncEntitySelect';
import ExecutionViewerDialog from '../execution/ExecutionViewerDialog';
import ExecutionHistoryPanel from '../execution/ExecutionHistoryPanel';
import { TestExpertDialog } from '../expert/TestExpertDialog';
import { SchedulePanel } from './SchedulePanel';
import { BacktestPanel } from './BacktestPanel';
import PageBackButton from '../../../components/PageBackButton';
import TradesPanel from './TradesPanel';
import AnalyticsPanel from './AnalyticsPanel';
import StoragePanel from './StoragePanel';
import SharedInputsPanel from './SharedInputsPanel';
import OutputSchemaEditor from '../expert/OutputSchemaEditor';
import AIBuilder from './AIBuilder';
import { validationIssueSeverity } from './AISystemCanvas';
import ExpertEditorPanel from './ExpertEditorPanel';
import HelpDocumentationPanel from './HelpDocumentationPanel';
import { useAISystemSocket } from '../../hooks/useAISystemSocket';
import { AI_ACTION_SURFACES } from '../../../access/surfaces';
import { useActionAccess } from '../../../access/actionAccess';

// Validation status type
interface ValidationStatus {
  hasError?: boolean;
  hasWarning?: boolean;
}

const SYSTEM_TABS = {
  BUILDER: 0,
  RUN_AUTOMATE: 1,
  ACTIVITY: 2,
  ADVANCED: 3,
} as const;

const RUN_AUTOMATE_TABS = {
  SCHEDULES: 0,
  MANUAL_RUN: 1,
  BACKTESTING: 2,
} as const;

const ACTIVITY_TABS = {
  RUNS: 0,
  TRADES: 1,
  INSIGHTS: 2,
} as const;

const ADVANCED_TABS = {
  SHARED_DATA: 0,
  STORAGE: 1,
  VALIDATION: 2,
  HELP: 3,
} as const;

const scrollableTabsSx = {
  maxWidth: '100%',
  '& .MuiTabs-scroller': {
    overflowX: 'auto !important',
    scrollbarWidth: 'none',
    WebkitOverflowScrolling: 'touch',
    '&::-webkit-scrollbar': { display: 'none' },
  },
  '& .MuiTabs-flexContainer': {
    flexWrap: 'nowrap',
  },
  '& .MuiTabs-list': {
    flexWrap: 'nowrap',
  },
  '& .MuiTab-root': {
    flexShrink: 0,
  },
  '& .MuiTabs-scrollButtons': {
    display: 'none',
  },
};

const getAgentModelLabel = (agent?: any) => (
  agent?.model_name ||
  agent?.model_display ||
  agent?.model?.name ||
  agent?.model?.model ||
  'No model'
);

const getAgentSelectorLabel = (agent?: any) => (
  agent ? `${agent.name} • ${getAgentModelLabel(agent)}` : 'Select builder agent'
);

// Custom Expert Node Component
const ExpertNode: React.FC<NodeProps> = ({ data, selected }) => {
  const expert = data.expert as ExpertListItem;
  const validation = (data.validation as ValidationStatus) || {};
  const isDark = data.isDark as boolean || false;
  const runState = data.runState as CanvasExpertRunState | undefined;
  const onViewExpertRun = data.onViewExpertRun as ((expertId: number) => void) | undefined;
  const runStatus = runState?.status;
  const runStatusColor = getCanvasRunStatusColor(runStatus);
  const runStatusBackground = getCanvasRunStatusBackground(runStatus, isDark);
  const canViewRunDetails = Boolean(
    runState?.executionId && runStatus && !['pending', 'running'].includes(runStatus) && onViewExpertRun
  );
  const hasForLoop = Boolean(expert.for_loop && Object.keys(expert.for_loop).length > 0);
  const loopSource = hasForLoop && typeof expert.for_loop?.source === 'string'
    ? expert.for_loop.source
    : 'source not set';
  const loopAlias = hasForLoop && typeof expert.for_loop?.item_alias === 'string'
    ? expert.for_loop.item_alias
    : 'item';
  const loopRunLabel = runState?.loopIterationsTotal
    ? `Loop ${runState.loopIterationsCompleted ?? 0}/${runState.loopIterationsTotal}`
    : runStatus === 'running'
      ? 'Loop running'
      : 'Loop';
  
  // Check if expert uses indicator sets from data passed in
  // This will be populated when we enhance the node creation
  const hasIndicatorSets = data.hasIndicatorSets as boolean || false;
  const indicatorSetCount = data.indicatorSetCount as number || 0;

  const renderNodeStatus = () => {
    if (runStatus) {
      const label = getCanvasRunStatusLabel(runStatus);

      return (
        <Box display="flex" alignItems="center" gap={0.25} flexShrink={0}>
          <Tooltip title={label}>
            <Box display="flex" alignItems="center">
              {runStatus === 'running' && <CircularProgress size={18} thickness={5} />}
              {runStatus === 'completed' && <ValidIcon fontSize="small" color="success" />}
              {runStatus === 'error' && <ErrorIcon fontSize="small" color="error" />}
              {runStatus === 'skipped' && <WarningIcon fontSize="small" color="warning" />}
              {runStatus === 'stopped' && <StopIcon fontSize="small" color="warning" />}
              {runStatus === 'pending' && <QueuedIcon fontSize="small" color="warning" />}
            </Box>
          </Tooltip>
          {canViewRunDetails && (
            <Tooltip title="View execution details">
              <IconButton
                size="small"
                aria-label={`View execution details for ${expert.name}`}
                onClick={(event) => {
                  event.stopPropagation();
                  onViewExpertRun?.(expert.id);
                }}
                sx={{ width: 24, height: 24 }}
              >
                <ViewIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      );
    }

    if (validation.hasError) return <ErrorIcon fontSize="small" color="error" />;
    if (validation.hasWarning) return <WarningIcon fontSize="small" color="warning" />;
    if (expert.is_active) return <ValidIcon fontSize="small" color="success" />;
    return null;
  };
  
  return (
    <Box
      sx={{
        padding: 2,
        position: 'relative',
        overflow: 'hidden',
        borderRadius: 2,
        border: 2,
        borderColor: selected 
          ? 'primary.main' 
          : runStatusColor || (validation.hasError
            ? 'error.main' 
            : validation.hasWarning 
              ? 'warning.main' 
              : expert.expert_type === 'ANALYSIS' 
                ? 'primary.light' 
                : expert.expert_type === 'CODE'
                  ? 'warning.light'
                  : expert.expert_type === 'AGENT'
                    ? 'info.light'
                    : 'secondary.light'),
        background: selected 
          ? (isDark ? 'rgba(25, 118, 210, 0.2)' : 'primary.50') 
          : runStatusBackground || (isDark ? '#2d2d2d' : '#fff'),
        minWidth: 220,
        boxShadow: selected
          ? 4
          : runStatus === 'running'
            ? '0 0 0 3px rgba(25, 118, 210, 0.14), 0 8px 18px rgba(25, 118, 210, 0.18)'
          : 2,
        animation: runStatus === 'running'
          ? 'canvas-node-running-pulse 1.35s ease-in-out infinite'
            : undefined,
        transition: 'all 0.2s',
        '&:hover': {
          boxShadow: 6,
        },
        '&::after': runStatus ? {
          content: '""',
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 3,
          pointerEvents: 'none',
          background: runStatus === 'running'
            ? 'linear-gradient(90deg, #1976d2 0%, #90caf9 50%, #1976d2 100%)'
            : runStatus === 'completed'
              ? '#2e7d32'
            : runStatus === 'error'
              ? '#d32f2f'
            : '#ed6c02',
          backgroundSize: runStatus === 'running' ? '200% 100%' : undefined,
          animation: runStatus === 'running' ? 'canvas-node-running-bar 0.9s linear infinite' : undefined,
        } : undefined,
        '@keyframes canvas-node-running-pulse': {
          '0%, 100%': {
            boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.14), 0 8px 18px rgba(25, 118, 210, 0.18)',
          },
          '50%': {
            boxShadow: '0 0 0 6px rgba(25, 118, 210, 0.07), 0 10px 24px rgba(25, 118, 210, 0.22)',
          },
        },
        '@keyframes canvas-node-running-bar': {
          '0%': {
            backgroundPosition: '200% 0',
          },
          '100%': {
            backgroundPosition: '0 0',
          },
        },
      }}
    >
      {/* Input Handles */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: isDark ? '#888' : '#555',
          width: 12,
          height: 12,
          border: isDark ? '2px solid #333' : '2px solid #fff',
        }}
      />
      
      {/* Node Header */}
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1, mr: 1 }}>
          {expert.name}
        </Typography>
        {renderNodeStatus()}
      </Box>
      
      {/* Node Type Badge */}
      <Chip
        label={expert.expert_type_display}
        size="small"
        color={
          expert.expert_type === 'ANALYSIS' 
            ? 'primary' 
            : expert.expert_type === 'CODE' 
              ? 'warning' 
              : expert.expert_type === 'AGENT'
                ? 'info'
                : 'secondary'
        }
        sx={{ mb: 1, fontSize: '0.7rem' }}
      />
      
      {/* Model Information (CODE and AGENT experts don't show a model) */}
      {expert.expert_type !== 'CODE' && expert.expert_type !== 'AGENT' && (
        <Typography variant="caption" display="block" color="text.secondary" sx={{ mb: 0.5 }}>
          Model: {expert.model_name || 'Not set'}
        </Typography>
      )}
      
      {/* Indicator Set Information */}
      {hasIndicatorSets && (
        <Box sx={{ mb: 0.5 }}>
          <Chip
            icon={<ShowChartIcon />}
            label={`${indicatorSetCount} Indicator Set${indicatorSetCount > 1 ? 's' : ''}`}
            size="small"
            color="info"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Box>
      )}
      
      {/* Cache Information */}
      {expert.use_cache && (
        <Box sx={{ mb: 0.5 }}>
          <Chip
            label="Cached"
            size="small"
            color="primary"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Box>
      )}
      
      {/* Status Indicators */}
      <Box display="flex" gap={0.5} flexWrap="wrap">
        {expert.is_active ? (
          <Chip label="Active" size="small" color="success" variant="outlined" />
        ) : (
          <Chip label="Inactive" size="small" color="default" variant="outlined" />
        )}
        {expert.dry_run && (
          <Chip label="Dry Run" size="small" color="info" variant="outlined" />
        )}
        {hasForLoop && (
          <Tooltip title={`For loop: ${loopSource} as ${loopAlias}`}>
            <Chip
              label={loopRunLabel}
              size="small"
              color="secondary"
              variant={runState?.loopIterationsTotal || runStatus === 'running' ? 'filled' : 'outlined'}
            />
          </Tooltip>
        )}
      </Box>
      
      {/* Output Handles */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: isDark ? '#888' : '#555',
          width: 12,
          height: 12,
          border: isDark ? '2px solid #333' : '2px solid #fff',
        }}
      />
    </Box>
  );
};

// Node types configuration
const nodeTypes = {
  expertNode: ExpertNode,
};

// Custom Edge with Delete Button
const DeletableEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  selected,
  style,
}) => {
  const { setEdges } = useReactFlow();
  const adjustedTarget = offsetEdgeEndpoint(targetX, targetY, targetPosition);
  const edgeColor = selected ? SELECTED_EDGE_COLOR : DEFAULT_EDGE_COLOR;
  const [edgePath, labelX, labelY] = getSmoothStepPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX: adjustedTarget.x,
    targetY: adjustedTarget.y,
    targetPosition,
  });
  const arrowPath = getEdgeArrowPath(adjustedTarget.x, adjustedTarget.y, targetPosition);

  const onEdgeDelete = (event: React.MouseEvent) => {
    event.stopPropagation();
    setEdges((edges) => edges.filter((edge) => edge.id !== id));
  };

  return (
    <>
      <BaseEdge
        path={edgePath}
        style={{
          ...style,
          strokeWidth: selected ? 3 : 2,
          stroke: edgeColor,
        }}
      />
      <path d={arrowPath} fill={edgeColor} stroke="none" pointerEvents="none" />
      {selected && (
        <EdgeLabelRenderer>
          <Box
            sx={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            className="nodrag nopan"
          >
            <IconButton
              size="small"
              onClick={onEdgeDelete}
              sx={{
                backgroundColor: '#ff6b6b',
                color: 'white',
                width: 24,
                height: 24,
                '&:hover': {
                  backgroundColor: '#ff4444',
                },
                boxShadow: 2,
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

// Edge types configuration
const edgeTypes = {
  deletable: DeletableEdge,
};

const DEFAULT_EDGE_COLOR = '#2196f3';
const SELECTED_EDGE_COLOR = '#ff6b6b';

const createEdgeMarker = (color: string) => ({
  type: MarkerType.ArrowClosed,
  width: 20,
  height: 20,
  color,
});

const EDGE_MARKER_OFFSET = 20;
const EDGE_ARROW_LENGTH = 14;
const EDGE_ARROW_WIDTH = 12;

const offsetEdgeEndpoint = (x: number, y: number, position?: Position) => {
  switch (position) {
    case Position.Left:
      return { x: x - EDGE_MARKER_OFFSET, y };
    case Position.Right:
      return { x: x + EDGE_MARKER_OFFSET, y };
    case Position.Top:
      return { x, y: y - EDGE_MARKER_OFFSET };
    case Position.Bottom:
      return { x, y: y + EDGE_MARKER_OFFSET };
    default:
      return { x, y };
  }
};

const getEdgeArrowPath = (baseX: number, baseY: number, position?: Position) => {
  switch (position) {
    case Position.Left:
      return `M ${baseX} ${baseY - EDGE_ARROW_WIDTH / 2} L ${baseX} ${baseY + EDGE_ARROW_WIDTH / 2} L ${baseX + EDGE_ARROW_LENGTH} ${baseY} Z`;
    case Position.Right:
      return `M ${baseX} ${baseY - EDGE_ARROW_WIDTH / 2} L ${baseX} ${baseY + EDGE_ARROW_WIDTH / 2} L ${baseX - EDGE_ARROW_LENGTH} ${baseY} Z`;
    case Position.Top:
      return `M ${baseX - EDGE_ARROW_WIDTH / 2} ${baseY} L ${baseX + EDGE_ARROW_WIDTH / 2} ${baseY} L ${baseX} ${baseY + EDGE_ARROW_LENGTH} Z`;
    case Position.Bottom:
      return `M ${baseX - EDGE_ARROW_WIDTH / 2} ${baseY} L ${baseX + EDGE_ARROW_WIDTH / 2} ${baseY} L ${baseX} ${baseY - EDGE_ARROW_LENGTH} Z`;
    default:
      return `M ${baseX} ${baseY - EDGE_ARROW_WIDTH / 2} L ${baseX} ${baseY + EDGE_ARROW_WIDTH / 2} L ${baseX + EDGE_ARROW_LENGTH} ${baseY} Z`;
  }
};

// Edge configuration
const defaultEdgeOptions = {
  type: 'smoothstep',
  markerEnd: createEdgeMarker(DEFAULT_EDGE_COLOR),
  style: {
    strokeWidth: 2,
    stroke: DEFAULT_EDGE_COLOR,
  },
  // Enable edge selection and deletion
  focusable: true,
  deletable: true,
  selectable: true,
  // Increase interaction width for easier selection
  interactionWidth: 20,
};

// Custom edge styles for selected state
const selectedEdgeStyle = {
  strokeWidth: 3,
  stroke: SELECTED_EDGE_COLOR,
};

// Validation types
interface ValidationIssue {
  type: 'error' | 'warning' | 'info';
  nodeId?: string;
  message: string;
}

type CanvasExpertRunStatus = 'pending' | 'running' | 'completed' | 'skipped' | 'stopped' | 'error';

interface CanvasExpertRunState {
  status: CanvasExpertRunStatus;
  executionId?: string | null;
  totalCost?: number;
  result?: Record<string, any>;
  loopIterationsCompleted?: number;
  loopIterationsTotal?: number;
}

const getCanvasRunStatusColor = (status?: CanvasExpertRunStatus) => {
  switch (status) {
    case 'running':
      return 'primary.main';
    case 'completed':
      return 'success.main';
    case 'error':
      return 'error.main';
    case 'skipped':
    case 'stopped':
      return 'warning.main';
    case 'pending':
      return 'warning.main';
    default:
      return undefined;
  }
};

const getCanvasRunStatusBackground = (status?: CanvasExpertRunStatus, isDark?: boolean) => {
  switch (status) {
    case 'running':
      return isDark ? 'rgba(25, 118, 210, 0.14)' : 'rgba(25, 118, 210, 0.06)';
    case 'completed':
      return isDark ? 'rgba(46, 125, 50, 0.12)' : 'rgba(46, 125, 50, 0.05)';
    case 'error':
      return isDark ? 'rgba(211, 47, 47, 0.14)' : 'rgba(211, 47, 47, 0.06)';
    case 'skipped':
    case 'stopped':
    case 'pending':
      return isDark ? 'rgba(237, 108, 2, 0.1)' : 'rgba(237, 108, 2, 0.05)';
    default:
      return undefined;
  }
};

const getCanvasRunStatusLabel = (status?: CanvasExpertRunStatus) => {
  switch (status) {
    case 'running':
      return 'Running';
    case 'completed':
      return 'Done';
    case 'error':
      return 'Failed';
    case 'skipped':
      return 'Skipped';
    case 'stopped':
      return 'Stopped';
    case 'pending':
      return 'Queued';
    default:
      return '';
  }
};

const getIndicatorSetInfo = (expert: Pick<ExpertListItem, 'input_data'> | Pick<Expert, 'input_data'>) => {
  const inputs = Array.isArray(expert.input_data?.inputs) ? expert.input_data.inputs : [];
  const indicatorSetCount = inputs.filter((input: any) =>
    input.type === 'indicator' && (input.use_indicator_set || input.indicator_set_id)
  ).length;

  return {
    hasIndicatorSets: indicatorSetCount > 0,
    indicatorSetCount,
  };
};

// Auto-layout function using dagre
const getLayoutedElements = (nodes: Node[], edges: Edge[], direction = 'LR') => {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({ rankdir: direction, ranksep: 100, nodesep: 80 });

  nodes.forEach((node) => {
    dagreGraph.setNode(node.id, { width: 250, height: 150 });
  });

  edges.forEach((edge) => {
    dagreGraph.setEdge(edge.source, edge.target);
  });

  dagre.layout(dagreGraph);

  const layoutedNodes = nodes.map((node) => {
    const nodeWithPosition = dagreGraph.node(node.id);
    return {
      ...node,
      position: {
        x: nodeWithPosition.x - 125,
        y: nodeWithPosition.y - 75,
      },
    };
  });

  return { nodes: layoutedNodes, edges };
};

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  // Builder tab needs hidden overflow for its internal split pane scrolling.
  // Other tabs need auto overflow to scroll their content
  const isBuilderTab = index === SYSTEM_TABS.BUILDER;
  
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`system-tabpanel-${index}`}
      aria-labelledby={`system-tab-${index}`}
      style={{
        flex: 1,
        minHeight: 0,
        display: value === index ? 'flex' : 'none',
        flexDirection: 'column',
        overflow: isBuilderTab ? 'hidden' : 'auto',
        width: '100%',
      }}
      {...other}
    >
      <Box sx={{ 
        flex: 1, 
        minHeight: 0, 
        display: 'flex', 
        flexDirection: 'column', 
        overflow: isBuilderTab ? 'hidden' : 'auto',
        width: '100%',
      }}>
        {children}
      </Box>
    </div>
  );
}

// Main Canvas Component (needs ReactFlow context)
const AISystemCanvas: React.FC<{
  nodes: Node[];
  edges: Edge[];
  onNodesChange: any;
  onEdgesChange: any;
  onConnect: any;
  onNodeClick: (event: React.MouseEvent, node: Node) => void;
  onAutoLayout: () => void;
  expertsData: any;
  addExpertToCanvas: (expert: ExpertListItem) => void;
  setExpertFormOpen: (open: boolean) => void;
  selectedNode: Node | null;
  selectedNodeStartInEditMode: boolean;
  setSelectedNode: React.Dispatch<React.SetStateAction<Node | null>>;
  setSelectedNodeStartInEditMode: (value: boolean) => void;
  setNodes: React.Dispatch<React.SetStateAction<Node[]>>;
  setEdges: React.Dispatch<React.SetStateAction<Edge[]>>;
  refetchExperts: () => void;
  validationIssues: ValidationIssue[];
  onSaveCanvas?: () => void | Promise<void>;
  isSavingCanvas?: boolean;
  onRunCanvas?: () => void | Promise<void>;
  onStopCanvas?: () => void | Promise<void>;
  isRunningCanvas?: boolean;
  isStoppingCanvas?: boolean;
  canRunCanvas?: boolean;
  runDisabledReason?: string;
  expertRunStates?: Record<string, CanvasExpertRunState>;
  onViewExpertRun?: (expertId: number) => void;
  isMobile?: boolean;
  isDark?: boolean;
}> = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onConnect,
  onNodeClick,
  onAutoLayout,
  expertsData,
  addExpertToCanvas,
  setExpertFormOpen,
  selectedNode,
  selectedNodeStartInEditMode,
  setSelectedNode,
  setSelectedNodeStartInEditMode,
  setNodes,
  setEdges,
  refetchExperts,
  validationIssues,
  onSaveCanvas,
  isSavingCanvas = false,
  onRunCanvas,
  onStopCanvas,
  isRunningCanvas = false,
  isStoppingCanvas = false,
  canRunCanvas = true,
  runDisabledReason,
  expertRunStates,
  onViewExpertRun,
  isMobile = false,
  isDark = false,
}) => {
  const reactFlow = useReactFlow();
  const [searchTerm, setSearchTerm] = useState('');
  const [isInitialized, setIsInitialized] = useState(false);
  const [showLibrary, setShowLibrary] = useState(!isMobile); // Hide library by default on mobile

  const handleZoomIn = () => reactFlow.zoomIn();
  const handleZoomOut = () => reactFlow.zoomOut();
  const handleFitView = () => reactFlow.fitView({ padding: 0.3, maxZoom: 1 });

  // Handle ReactFlow initialization
  const onInit = useCallback(() => {
    setIsInitialized(true);
  }, []);

  // Fit view when nodes change and ReactFlow is initialized
  useEffect(() => {
    if (isInitialized && nodes.length > 0) {
      // Small delay to ensure nodes are rendered
      const timer = setTimeout(() => {
        reactFlow.fitView({ padding: 0.3, maxZoom: 1 });
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [isInitialized, nodes, reactFlow]);

  // Filter experts by search term
  const filteredExperts = useMemo(() => {
    if (!expertsData?.results) return [];
    if (!searchTerm) return expertsData.results;
    
    const term = searchTerm.toLowerCase();
    return expertsData.results.filter((expert: ExpertListItem) =>
      expert.name.toLowerCase().includes(term) ||
      expert.expert_type.toLowerCase().includes(term) ||
      expert.model_name?.toLowerCase().includes(term)
    );
  }, [expertsData, searchTerm]);

  const groupedExperts = useMemo(() => {
    const onCanvas: ExpertListItem[] = [];
    const available: ExpertListItem[] = [];

    filteredExperts.forEach((expert: ExpertListItem) => {
      const isOnCanvas = nodes.some((node) => node.id === expert.id.toString());
      if (isOnCanvas) {
        onCanvas.push(expert);
      } else {
        available.push(expert);
      }
    });

    return { onCanvas, available };
  }, [filteredExperts, nodes]);

  const openExpertFromLibrary = useCallback((expert: ExpertListItem) => {
    const existingNode = nodes.find((node) => node.id === expert.id.toString());

    if (existingNode) {
      setSelectedNode(existingNode);
    } else {
      setSelectedNode({
        id: `library-${expert.id}`,
        type: 'expert',
        position: { x: 0, y: 0 },
        data: {
          expert,
          isDark,
        },
      } as Node);
    }

    setSelectedNodeStartInEditMode(true);

    if (isMobile) {
      setShowLibrary(false);
    }
  }, [isDark, isMobile, nodes, setSelectedNode, setSelectedNodeStartInEditMode]);

  const runControlTitle = isRunningCanvas
    ? isStoppingCanvas
      ? 'Stopping run...'
      : onStopCanvas
        ? 'Stop this run'
        : 'Manual run in progress'
    : runDisabledReason || 'Run this AI system';
  const handleRunControlClick = isRunningCanvas && onStopCanvas ? onStopCanvas : onRunCanvas;
  const runControlDisabled = isRunningCanvas ? !onStopCanvas || isStoppingCanvas : !canRunCanvas;
  const runControlIcon = isRunningCanvas
    ? isStoppingCanvas
      ? <CircularProgress size={14} color="inherit" />
      : <StopIcon fontSize="small" />
    : <PlayArrowIcon fontSize="small" />;
  const runControlLabel = isRunningCanvas ? (isStoppingCanvas ? 'Stopping' : 'Stop') : 'Run';

  return (
    <Box 
      display="flex" 
      gap={2} 
      height="100%"
      flex={1}
      minHeight={0}
      flexDirection={{ xs: 'column', md: 'row' }}
      position="relative"
    >
      {/* Expert Library Sidebar - collapsible */}
      {showLibrary && (
        <Paper sx={{ 
          width: { xs: '100%', md: 300 }, 
          maxHeight: { xs: '40vh', md: 'none' },
          display: 'flex', 
          flexDirection: 'column', 
          overflow: 'hidden',
          position: { xs: 'relative', md: 'relative' },
          zIndex: { xs: 5, md: 'auto' },
        }}>
          <Box p={{ xs: 1.5, sm: 2 }} borderBottom={1} borderColor="divider">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Box>
                <Typography variant={isMobile ? 'body1' : 'h6'}>Expert Library</Typography>
                <Typography variant="caption" color="text.secondary">
                  Click a row to edit. Use + to place an expert on the canvas.
                </Typography>
              </Box>
              <Box display="flex" gap={0.5}>
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setExpertFormOpen(true)}
                >
                  New
                </Button>
              </Box>
            </Box>
            
            <TextField
              size="small"
              placeholder="Search experts to edit..."
              fullWidth
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Box>
          
          <Box flex={1} overflow="auto" p={1}>
            {filteredExperts.length > 0 ? (
              <Stack spacing={1}>
                {groupedExperts.onCanvas.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      On Canvas
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {groupedExperts.onCanvas.map((expert: ExpertListItem) => (
                        <ListItem key={expert.id} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => openExpertFromLibrary(expert)}
                            sx={{
                              borderRadius: 1,
                              px: 1.25,
                              py: 0.9,
                              borderLeft: 2,
                              borderColor: 'success.main',
                              bgcolor: 'action.hover',
                              '&:hover': { bgcolor: 'action.selected' },
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1} width="100%">
                              <ListItemText
                                primaryTypographyProps={{ component: 'div' }}
                                secondaryTypographyProps={{ component: 'div' }}
                                primary={
                                  <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap">
                                    <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
                                      {expert.name}
                                    </Typography>
                                    <Chip label="On canvas" size="small" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.68rem' }} />
                                  </Box>
                                }
                                secondary={
                                  <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                    <Chip
                                      label={expert.expert_type_display}
                                      size="small"
                                      color={
                                        expert.expert_type === 'ANALYSIS'
                                          ? 'primary'
                                          : expert.expert_type === 'CODE'
                                            ? 'warning'
                                            : expert.expert_type === 'AGENT'
                                              ? 'info'
                                              : 'secondary'
                                      }
                                      sx={{ fontSize: '0.68rem', height: 18 }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
                                      {expert.model_name || 'No model set'}
                                    </Typography>
                                  </Box>
                                }
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                Edit
                              </Typography>
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}

                {groupedExperts.available.length > 0 && (
                  <Box>
                    <Typography variant="caption" color="text.secondary" sx={{ px: 1, display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                      Available
                    </Typography>
                    <List dense sx={{ p: 0 }}>
                      {groupedExperts.available.map((expert: ExpertListItem) => (
                        <ListItem key={expert.id} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => openExpertFromLibrary(expert)}
                            sx={{
                              borderRadius: 1,
                              px: 1.25,
                              py: 0.9,
                              border: 1,
                              borderColor: 'divider',
                              '&:hover': { bgcolor: 'action.hover' },
                            }}
                          >
                            <Box display="flex" alignItems="center" gap={1} width="100%">
                              <ListItemText
                                primaryTypographyProps={{ component: 'div' }}
                                secondaryTypographyProps={{ component: 'div' }}
                                primary={
                                  <Typography variant="body2" component="span" sx={{ fontWeight: 500 }}>
                                    {expert.name}
                                  </Typography>
                                }
                                secondary={
                                  <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ mt: 0.5 }}>
                                    <Chip
                                      label={expert.expert_type_display}
                                      size="small"
                                      color={
                                        expert.expert_type === 'ANALYSIS'
                                          ? 'primary'
                                          : expert.expert_type === 'CODE'
                                            ? 'warning'
                                            : expert.expert_type === 'AGENT'
                                              ? 'info'
                                              : 'secondary'
                                      }
                                      sx={{ fontSize: '0.68rem', height: 18 }}
                                    />
                                    <Typography variant="caption" color="text.secondary" sx={{ minWidth: 0 }}>
                                      {expert.model_name || 'No model set'}
                                    </Typography>
                                  </Box>
                                }
                              />
                              <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                                Edit
                              </Typography>
                              <IconButton
                                size="small"
                                color="primary"
                                title="Add to canvas"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  addExpertToCanvas(expert);
                                  if (isMobile) setShowLibrary(false);
                                }}
                              >
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          </ListItemButton>
                        </ListItem>
                      ))}
                    </List>
                  </Box>
                )}
              </Stack>
            ) : (
              <Box p={2} textAlign="center">
                {searchTerm ? (
                  <Typography variant="body2" color="text.secondary">
                    No experts match "{searchTerm}"
                  </Typography>
                ) : (
                  <Alert severity="info">
                    No experts available. Click "New" to create your first expert!
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        </Paper>
      )}

      {/* Canvas Area */}
      <Box flex={1} position="relative" minHeight={{ xs: '300px', sm: '400px' }}>
        
        <Paper sx={{ 
          height: '100%', 
          position: 'relative', 
          bgcolor: isDark ? '#1a1a1a' : 'background.paper',
          // Dark mode styles for ReactFlow Controls (bottom-left zoom buttons)
          ...(isDark && {
            '& .react-flow__controls': {
              borderColor: '#555',
              boxShadow: '0 0 4px rgba(0,0,0,0.5)',
            },
            '& .react-flow__controls-button': {
              backgroundColor: '#2a2a2a',
              borderColor: '#555',
              fill: '#e0e0e0',
              '&:hover': {
                backgroundColor: '#3a3a3a',
              },
            },
            '& .react-flow__attribution': {
              backgroundColor: 'rgba(30, 30, 30, 0.7)',
              '& a': {
                color: '#888',
              },
            },
          }),
        }}>
          <ReactFlow
            nodes={nodes.map(node => ({
              ...node,
              data: {
                ...node.data,
                isDark,
                runState: expertRunStates?.[node.id],
                onViewExpertRun,
              },
            }))}
            edges={edges.map(edge => ({
              ...edge,
              type: edge.type || 'deletable',
              // Apply selected style when edge is selected
              style: edge.selected ? selectedEdgeStyle : edge.style || defaultEdgeOptions.style,
              markerEnd: edge.selected ? createEdgeMarker(SELECTED_EDGE_COLOR) : edge.markerEnd || createEdgeMarker(DEFAULT_EDGE_COLOR),
            }))}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            onInit={onInit}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={defaultEdgeOptions}
            deleteKeyCode={['Delete', 'Backspace']}
            multiSelectionKeyCode="Shift"
            panActivationKeyCode={null}
            elementsSelectable={true}
            selectNodesOnDrag={false}
            fitView
            fitViewOptions={{ padding: 0.3, maxZoom: 1 }}
            attributionPosition="bottom-left"
            minZoom={0.1}
            maxZoom={2}
            // Better touch support
            panOnDrag={true}
            panOnScroll={false}
            zoomOnScroll={true}
            zoomOnPinch={true}
          >
            <Background 
              variant={BackgroundVariant.Dots} 
              gap={12} 
              size={1} 
              color={isDark ? '#555' : undefined}
            />
            {!isMobile && (
              <MiniMap 
                nodeColor={(node) => {
                  const expert = (node.data as any)?.expert;
                  return expert?.expert_type === 'ANALYSIS' ? '#1976d2' : '#9c27b0';
                }}
                style={{
                  backgroundColor: isDark ? '#1e1e1e' : '#f5f5f5',
                }}
                maskColor={isDark ? 'rgba(0, 0, 0, 0.6)' : undefined}
              />
            )}
            
            {/* Canvas Toolbar - simplified on mobile */}
            <Panel position="top-left">
              <Paper sx={{ p: 0.5, display: 'flex', alignItems: 'center', gap: 1.25 }}>
                {(!isMobile || !showLibrary) && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => setShowLibrary((current) => !current)}
                    startIcon={showLibrary ? <ChevronLeftIcon /> : <ChevronRightIcon />}
                    sx={{ mr: 0.25 }}
                  >
                    {showLibrary ? 'Hide Library' : 'Library'}
                  </Button>
                )}
                <ButtonGroup size="small" variant="outlined">
                  <Button onClick={onAutoLayout} sx={{ minWidth: 36, px: 1 }} aria-label="Auto layout">
                    <AutoLayoutIcon fontSize="small" />
                  </Button>
                  <Button onClick={handleZoomIn} sx={{ minWidth: 36, px: 1 }} aria-label="Zoom in">
                    <ZoomInIcon fontSize="small" />
                  </Button>
                  <Button onClick={handleZoomOut} sx={{ minWidth: 36, px: 1 }} aria-label="Zoom out">
                    <ZoomOutIcon fontSize="small" />
                  </Button>
                  <Button onClick={handleFitView} sx={{ minWidth: 36, px: 1 }} aria-label="Fit canvas">
                    <FitScreenIcon fontSize="small" />
                  </Button>
                </ButtonGroup>
                {onSaveCanvas && (
                  <Button
                    size="small"
                    variant="contained"
                    onClick={onSaveCanvas}
                    disabled={isSavingCanvas}
                    startIcon={isSavingCanvas ? <CircularProgress size={14} color="inherit" /> : <SaveIcon fontSize="small" />}
                    sx={{ minHeight: 30, whiteSpace: 'nowrap' }}
                  >
                    Save
                  </Button>
                )}
                {onRunCanvas && (
                  <Tooltip title={runControlTitle}>
                    <span>
                      <Button
                        size="small"
                        variant={isRunningCanvas ? 'outlined' : 'contained'}
                        color={isRunningCanvas ? 'error' : 'success'}
                        onClick={handleRunControlClick}
                        disabled={runControlDisabled}
                        startIcon={runControlIcon}
                        sx={{ minHeight: 30, whiteSpace: 'nowrap' }}
                      >
                        {runControlLabel}
                      </Button>
                    </span>
                  </Tooltip>
                )}
              </Paper>
            </Panel>

            {/* Statistics Panel - hide on mobile */}
            {!isMobile && (
              <Panel position="top-right">
                <Paper
                  sx={{
                    px: 1.25,
                    py: 0.75,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    bgcolor: isDark ? 'grey.900' : 'background.paper',
                  }}
                >
                  <Typography variant="caption" fontWeight="bold" sx={{ fontSize: '0.7rem', whiteSpace: 'nowrap' }}>
                    Workflow Statistics
                  </Typography>
                  <Box display="flex" alignItems="center" gap={1.25}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                      Experts: <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>{nodes.length}</Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                      Connections: <Box component="span" sx={{ color: 'text.primary', fontWeight: 700 }}>{edges.length}</Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                      Analysis: <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>{nodes.filter((n) => (n.data as any)?.expert?.expert_type === 'ANALYSIS').length}</Box>
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
                      Action: <Box component="span" sx={{ color: 'secondary.main', fontWeight: 700 }}>{nodes.filter((n) => (n.data as any)?.expert?.expert_type === 'ACTION').length}</Box>
                    </Typography>
                  </Box>
                </Paper>
              </Panel>
            )}

            {/* Validation Issues Panel - compact on mobile */}
            {validationIssues.length > 0 && (
              <Panel position="bottom-left">
                <Paper sx={{ p: 1, maxWidth: { xs: 280, sm: 400 }, bgcolor: isDark ? 'grey.900' : 'background.paper' }}>
                  <Typography variant="caption" display="block" fontWeight="bold" mb={0.5}>
                    <Badge
                      badgeContent={validationIssues.length}
                      color={validationIssueSeverity(validationIssues)}
                      sx={{ mr: 2 }}
                    >
                      {validationIssueSeverity(validationIssues) === 'info' ? 'Info' : 'Issues'}
                    </Badge>
                  </Typography>
                  <Box maxHeight={{ xs: 80, sm: 150 }} overflow="auto">
                    {validationIssues.slice(0, isMobile ? 2 : validationIssues.length).map((issue, idx) => (
                      <Alert key={idx} severity={issue.type} sx={{ mb: 0.5, py: 0, fontSize: '0.75rem' }}>
                        <Typography variant="caption">{issue.message}</Typography>
                      </Alert>
                    ))}
                    {isMobile && validationIssues.length > 2 && (
                      <Typography variant="caption" color="text.secondary">
                        +{validationIssues.length - 2} more issues
                      </Typography>
                    )}
                  </Box>
                </Paper>
              </Panel>
            )}

          </ReactFlow>
        </Paper>
      </Box>

      {/* Expert Editor Sidebar */}
      {selectedNode && (
        <ExpertEditorPanel
          selectedNode={selectedNode}
          edges={edges}
          expertsData={expertsData}
          startInEditMode={selectedNodeStartInEditMode}
          onClose={() => {
            setSelectedNodeStartInEditMode(false);
            setSelectedNode(null);
          }}
          onUpdate={async (updatedExpert) => {
            if (updatedExpert === null) {
              // Handle deletion: remove node and connected edges
              setNodes((nds) => nds.filter((node) => node.id !== selectedNode.id));
              setEdges((eds) => eds.filter((edge) => edge.source !== selectedNode.id && edge.target !== selectedNode.id));
              setSelectedNodeStartInEditMode(false);
              setSelectedNode(null);
            } else {
              const { hasIndicatorSets, indicatorSetCount } = getIndicatorSetInfo(updatedExpert);
              
              // Update node data on canvas
              setNodes((nds) =>
                nds.map((node) => {
                  if (node.id === selectedNode.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        expert: updatedExpert,
                        hasIndicatorSets,
                        indicatorSetCount,
                        label: (
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {updatedExpert.name}
                            </Typography>
                            <Chip
                              label={updatedExpert.expert_type}
                              size="small"
                              color={updatedExpert.expert_type === 'ANALYSIS' ? 'primary' : 'secondary'}
                              sx={{ mt: 0.5 }}
                            />
                          </Box>
                        ),
                      },
                    };
                  }
                  return node;
                })
              );

              setSelectedNode((current) => current ? {
                ...current,
                data: {
                  ...current.data,
                  expert: updatedExpert,
                  hasIndicatorSets,
                  indicatorSetCount,
                },
              } : current);
            }

            setSelectedNodeStartInEditMode(false);
            // Refetch experts to update the library
            refetchExperts();
          }}
        />
      )}
    </Box>
  );
};

// Main Form Component
export const AISystemFormAdvanced: React.FC<{
  open: boolean;
  onClose: () => void;
  system?: AISystem;
  onSystemUpdate?: () => void | Promise<void>; // Callback when system data changes
  asPage?: boolean; // If true, render as page instead of dialog
}> = ({ open, onClose, system, onSystemUpdate, asPage = false }) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const systemManualRunAccess = useActionAccess(AI_ACTION_SURFACES.systemTestRun);
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [activeTab, setActiveTab] = useState<number>(SYSTEM_TABS.BUILDER);
  const [runAutomateTab, setRunAutomateTab] = useState<number>(RUN_AUTOMATE_TABS.SCHEDULES);
  const [activityTab, setActivityTab] = useState<number>(ACTIVITY_TABS.RUNS);
  const [advancedTab, setAdvancedTab] = useState<number>(ADVANCED_TABS.SHARED_DATA);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [workflowNoticeExpanded, setWorkflowNoticeExpanded] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(true);
  const [isInitialMount, setIsInitialMount] = useState(true);

  const isAdmin = useMemo(() => {
    try {
      const stored = localStorage.getItem('user');
      if (stored) {
        const parsed = JSON.parse(stored);
        return parsed?.is_staff === true;
      }
    } catch {
      // Ignore malformed cached profile and treat as non-admin.
    }
    return false;
  }, []);
  
  // Sidebar offset is managed via CSS variable --sidebar-offset (set by .sidebar-open class)
  
  const handleCloseRequest = useCallback(() => {
    onClose();
  }, [onClose]);
  
  const [expertFormOpen, setExpertFormOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [expertToDelete, setExpertToDelete] = useState<ExpertListItem | null>(null);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [selectedNodeStartInEditMode, setSelectedNodeStartInEditMode] = useState(false);
  const [manualRunRunning, setManualRunRunning] = useState(false);
  const [executionViewerOpen, setExecutionViewerOpen] = useState(false);
  const [currentExecution, setCurrentExecution] = useState<AISystemExecution | null>(null);
  const [executionViewerInitialExpertId, setExecutionViewerInitialExpertId] = useState<number | null>(null);
  const [viewingExpert, setViewingExpert] = useState<Expert | null>(null);
  const [editingExpert, setEditingExpert] = useState<Expert | null>(null);
  
  // Inline edit state for system header fields

  const [formData, setFormData] = useState<AISystemCreate>({
    name: '',
    description: '',
    tags: [],
    is_active: true,
    is_template: false,
    is_public: false,
    prevent_concurrent_executions: false,
    concurrency_policy: 'queue',
    config: {},
  });
  const [canvasRefreshTrigger, setCanvasRefreshTrigger] = useState(0);
  const [lastAIUpdate, setLastAIUpdate] = useState<number>(0); // Timestamp of last AI update

  // Canvas state
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChangeInternal] = useEdgesState<Edge>([]);
  const [isSavingCanvas, setIsSavingCanvas] = useState(false);
  const [isStoppingCanvasRun, setIsStoppingCanvasRun] = useState(false);

  const handleCanvasExecutionStarted = useCallback((executionId: string) => {
    setIsStoppingCanvasRun(false);
    setCurrentExecution(null);
    setExecutionViewerInitialExpertId(null);
    toast.info('Manual run started on the canvas');
    queryClient.invalidateQueries({ queryKey: ['executions', system?.id] });
  }, [queryClient, system?.id]);

  const handleCanvasExecutionCompleted = useCallback((executionId: string, status: string) => {
    const normalizedStatus = (status || '').toLowerCase();
    queryClient.invalidateQueries({ queryKey: ['executions', system?.id] });
    executionApi.getSummary(executionId)
      .then((execution) => setCurrentExecution(execution))
      .catch((error) => console.error('Failed to refresh completed canvas execution:', error));
    if (['stopped', 'cancelled', 'canceled'].includes(normalizedStatus)) {
      toast.info('Manual run stopped');
    } else {
      toast.success('Manual run completed');
    }
  }, [queryClient, system?.id]);

  const handleCanvasExecutionError = useCallback((_executionId: string, error: string) => {
    queryClient.invalidateQueries({ queryKey: ['executions', system?.id] });
    toast.error(error || 'Manual run failed');
  }, [queryClient, system?.id]);

  const handleCanvasExecutionStopped = useCallback((executionId: string) => {
    setIsStoppingCanvasRun(false);
    queryClient.invalidateQueries({ queryKey: ['executions', system?.id] });
    if (executionId) {
      executionApi.getSummary(executionId)
        .then((execution) => setCurrentExecution(execution))
        .catch((error) => console.error('Failed to refresh stopped canvas execution:', error));
    }
    toast.info('Manual run stopped');
  }, [queryClient, system?.id]);

  const {
    isConnected: isCanvasRunConnected,
    isExecuting: isCanvasRunExecuting,
    currentExecutionId: canvasRunExecutionId,
    expertProgress: canvasExpertProgress,
    startExecution: startCanvasExecution,
    stopExecution: stopCanvasExecution,
  } = useAISystemSocket({
    systemId: system?.id || 0,
    autoConnect: Boolean(open && system?.id),
    onExecutionStarted: handleCanvasExecutionStarted,
    onExecutionCompleted: handleCanvasExecutionCompleted,
    onExecutionError: handleCanvasExecutionError,
    onExecutionStopped: handleCanvasExecutionStopped,
  });

  useEffect(() => {
    if (!isCanvasRunExecuting) {
      setIsStoppingCanvasRun(false);
    }
  }, [isCanvasRunExecuting]);

  // Wrap onEdgesChange to handle edge deletions
  const onEdgesChange = useCallback(
    async (changes: any[]) => {
      // Apply the changes first
      onEdgesChangeInternal(changes);
      
      // Check for removed edges
      const removedEdges = changes.filter((change) => change.type === 'remove');
      
      for (const change of removedEdges) {
        const edgeId = change.id;
        const edge = edges.find((e) => e.id === edgeId);
        
        if (edge) {
          const sourceNode = nodes.find((n) => n.id === edge.source);
          const targetNode = nodes.find((n) => n.id === edge.target);
          
          if (sourceNode && targetNode) {
            const sourceExpert = (sourceNode.data as any)?.expert;
            const targetExpert = (targetNode.data as any)?.expert;
            
            if (sourceExpert && targetExpert) {
              try {
                // Fetch the full target expert data
                const fullTargetExpert = await expertApi.get(targetExpert.id);
                
                // Remove the input corresponding to the source expert
                const currentInputs = fullTargetExpert.input_data?.inputs || [];
                const updatedInputs = currentInputs.filter(
                  (input: any) => !(input.type === 'expert' && input.expert_id === sourceExpert.id)
                );
                
                // Only update if something was removed
                if (updatedInputs.length < currentInputs.length) {
                  const updatedInputData = {
                    inputs: updatedInputs,
                  };
                  
                  // Update the expert via API
                  await expertApi.update(targetExpert.id, {
                    input_data: updatedInputData,
                  });
                  
                  // Update the node data in the canvas
                  setNodes((nds) =>
                    nds.map((node) =>
                      node.id === edge.target
                        ? {
                            ...node,
                            data: {
                              ...node.data,
                              expert: {
                                ...targetExpert,
                                input_data: updatedInputData,
                              },
                            },
                          }
                        : node
                    )
                  );
                  
                  toast.success(`Removed ${sourceExpert.name} input from ${targetExpert.name}`);
                }
              } catch (error) {
                console.error('Failed to update expert input_data on edge deletion:', error);
                toast.error('Connection removed, but failed to update expert inputs automatically');
              }
            }
          }
        }
      }
    },
    [onEdgesChangeInternal, edges, nodes, setNodes]
  );

  // Fetch available experts - only those belonging to this AI system
  const { data: expertsData, refetch: refetchExperts } = useQuery({
    queryKey: ['experts', system?.id],
    queryFn: () => expertApi.list(system?.id ? { 
      ai_system: system.id,
      page: 1,
      page_size: 200,
    } : {}),
    enabled: open && activeTab === 3,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const { data: canvasExpertsData, refetch: refetchCanvasExperts } = useQuery({
    queryKey: ['experts', system?.id, 'canvas-library'],
    queryFn: () => expertApi.list(system?.id ? {
      ai_system: system.id,
      page: 1,
      page_size: 1000,
    } : {}),
    enabled: open && !!system && activeTab === SYSTEM_TABS.BUILDER,
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

  const refetchCanvasLibraryExperts = useCallback(async () => {
    await refetchCanvasExperts();
    if (activeTab === 3) {
      await refetchExperts();
    }
  }, [activeTab, refetchExperts, refetchCanvasExperts]);

  // Fetch system builder agent for the AI-only Builder tab (default selection)
  const { data: builderAgent, isLoading: agentLoading, error: agentError } = useQuery({
    queryKey: ['system-builder-agent'],
    queryFn: () => agentApi.getSystemBuilder(),
    enabled: open && !!system && activeTab === SYSTEM_TABS.BUILDER,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

  // Fetch one page for the default; the selector itself loads searchable pages.
  const { data: allAgentsData } = useQuery({
    queryKey: ['all-agents-for-manager'],
    queryFn: () => agentApi.list(1, 25, { is_active: true, agent_type: 'AI_SYSTEM_BUILDER' }),
    enabled: open && !!system && activeTab === SYSTEM_TABS.BUILDER,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

  // State for selected agent in AI Manager
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(null);

  const systemBuilderAgents = useMemo(() => {
    const builderAgents = allAgentsData?.results || [];

    if (builderAgent && !builderAgents.some((agent: any) => agent.id === builderAgent.id)) {
      return [builderAgent, ...builderAgents];
    }

    return builderAgents;
  }, [allAgentsData, builderAgent]);

  // Set a default agent when the AI-only builder opens.
  useEffect(() => {
    const fallbackAgent =
      builderAgent?.agent_type === 'AI_SYSTEM_BUILDER'
        ? builderAgent
        : systemBuilderAgents[0];

    if (!fallbackAgent) return;

    if (!selectedAgentId) {
      setSelectedAgentId(fallbackAgent.id);
    }
  }, [builderAgent, selectedAgentId, systemBuilderAgents]);

  // Get the currently selected agent details
  const { data: selectedAgentDetail, isLoading: selectedAgentLoading } = useQuery({
    queryKey: ['agent-detail', selectedAgentId],
    queryFn: () => selectedAgentId ? agentApi.get(selectedAgentId) : null,
    enabled: !!selectedAgentId && selectedAgentId !== builderAgent?.id && open && !!system && activeTab === SYSTEM_TABS.BUILDER,
    staleTime: 60000,
    refetchOnWindowFocus: false,
  });

  const managerAgent = selectedAgentId && selectedAgentId !== builderAgent?.id
    ? selectedAgentDetail
    : builderAgent;

  const { data: persistedValidationReport } = useQuery<AISystemValidationReport>({
    queryKey: ['ai-system-validation', system?.id],
    queryFn: () => aiSystemApi.validate(system!.id),
    enabled: open && !!system?.id,
    staleTime: 15000,
    refetchOnWindowFocus: false,
  });

  // Validation logic
  const localValidationIssues = useMemo<ValidationIssue[]>(() => {
    const issues: ValidationIssue[] = [];
    
    // Check for disconnected nodes
    const connectedNodeIds = new Set<string>();
    edges.forEach(edge => {
      connectedNodeIds.add(edge.source);
      connectedNodeIds.add(edge.target);
    });
    
    nodes.forEach(node => {
      if (nodes.length > 1 && !connectedNodeIds.has(node.id)) {
        issues.push({
          type: 'warning',
          nodeId: node.id,
          message: `Expert "${(node.data as any)?.expert?.name}" is not connected to workflow`,
        });
      }
      
      const expert = (node.data as any)?.expert as ExpertListItem;
      if (!expert.is_active) {
        issues.push({
          type: 'warning',
          nodeId: node.id,
          message: `Expert "${expert.name}" is inactive`,
        });
      }
      
      // Model is required for ANALYSIS/ACTION types only
      // AGENT types use their agent's model, CODE types don't need a model
      if (expert.expert_type !== 'AGENT' && expert.expert_type !== 'CODE' && !expert.model_name) {
        issues.push({
          type: 'error',
          nodeId: node.id,
          message: `Expert "${expert.name}" has no model configured`,
        });
      }
      
      // AGENT type experts must have an agent configured
      if (expert.expert_type === 'AGENT' && !expert.agent) {
        issues.push({
          type: 'error',
          nodeId: node.id,
          message: `AGENT expert "${expert.name}" has no agent configured`,
        });
      }
      
      // CODE type experts must have code defined
      if (expert.expert_type === 'CODE' && (!expert.code || !expert.code.trim())) {
        issues.push({
          type: 'error',
          nodeId: node.id,
          message: `Expert "${expert.name}" has no Python code defined`,
        });
      }

      // Check config references ({{ inputName.key }}) in input definitions
      const inputData = expert.input_data;
      if (inputData) {
        const inputs: any[] = inputData.inputs || [];
        // Build map of expert-type input names -> expert_id
        const expertInputMap = new Map<string, number>();
        for (const inp of inputs) {
          if (inp.type === 'expert' && inp.name && inp.expert_id) {
            expertInputMap.set(inp.name, inp.expert_id);
          }
        }

        const refRegex = /\{\{\s*([\w]+)\.([\w]+)\s*\}\}/g;
        for (const inp of inputs) {
          if (inp.type === 'expert') continue;
          for (const [fieldKey, fieldVal] of Object.entries(inp)) {
            if (fieldKey === 'type' || fieldKey === 'name') continue;
            if (typeof fieldVal !== 'string') continue;
            let match: RegExpExecArray | null;
            refRegex.lastIndex = 0;
            while ((match = refRegex.exec(fieldVal)) !== null) {
              const refInputName = match[1];
              const refKey = match[2];

              if (!expertInputMap.has(refInputName)) {
                issues.push({
                  type: 'error',
                  nodeId: node.id,
                  message: `Expert "${expert.name}": config reference "{{ ${refInputName}.${refKey} }}" in "${inp.name || '?'}.${fieldKey}" refers to unknown expert input "${refInputName}"`,
                });
                continue;
              }

              // Check upstream connection exists
              const refExpertId = expertInputMap.get(refInputName)!;
              const hasUpstream = edges.some(
                e => e.source === String(refExpertId) && e.target === String(expert.id)
              );
              if (!hasUpstream) {
                const refExpertName = (nodes.find(n => n.id === String(refExpertId))?.data as any)?.expert?.name;
                issues.push({
                  type: 'warning',
                  nodeId: node.id,
                  message: `Expert "${expert.name}": config reference "{{ ${refInputName}.${refKey} }}" refers to expert "${refExpertName ?? refExpertId}" which is not connected upstream`,
                });
              }
            }
          }
        }
      }
    });
    
    // Check for circular dependencies
    const detectCycle = () => {
      const visited = new Set<string>();
      const recStack = new Set<string>();
      
      const dfs = (nodeId: string): boolean => {
        visited.add(nodeId);
        recStack.add(nodeId);
        
        const outgoingEdges = edges.filter(e => e.source === nodeId);
        for (const edge of outgoingEdges) {
          if (!visited.has(edge.target)) {
            if (dfs(edge.target)) return true;
          } else if (recStack.has(edge.target)) {
            return true;
          }
        }
        
        recStack.delete(nodeId);
        return false;
      };
      
      for (const node of nodes) {
        if (!visited.has(node.id)) {
          if (dfs(node.id)) return true;
        }
      }
      return false;
    };
    
    if (detectCycle()) {
      issues.push({
        type: 'error',
        message: 'Circular dependency detected in workflow',
      });
    }
    
    return issues;
  }, [nodes, edges]);

  const persistedValidationIssues = useMemo<ValidationIssue[]>(() => {
    if (!persistedValidationReport) return [];

    const mapIssue = (issue: any): ValidationIssue => ({
      type: issue.type === 'info' ? 'info' : issue.type === 'warning' ? 'warning' : 'error',
      nodeId: issue.expert_id != null ? String(issue.expert_id) : undefined,
      message: issue.message || 'Validation issue',
    });

    return [
      ...(persistedValidationReport.errors || []),
      ...(persistedValidationReport.warnings || []),
      ...(persistedValidationReport.info || []),
    ].map(mapIssue);
  }, [persistedValidationReport]);

  const validationIssues = useMemo<ValidationIssue[]>(() => {
    const seen = new Set<string>();
    const normalizeMessage = (message: string) => message.replace(/["']/g, '').replace(/\s+/g, ' ').trim().toLowerCase();
    const severityRank: Record<ValidationIssue['type'], number> = { error: 0, warning: 1, info: 2 };

    // Local messages mirror the backend validator's wording so the same issue dedupes to one entry.
    return [...localValidationIssues, ...persistedValidationIssues]
      .filter((issue) => {
        const key = `${issue.type}:${issue.nodeId || ''}:${normalizeMessage(issue.message)}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort((a, b) => severityRank[a.type] - severityRank[b.type]);
  }, [localValidationIssues, persistedValidationIssues]);

  useEffect(() => {
    setNodes((currentNodes) => {
      let changed = false;
      const nextNodes = currentNodes.map((node) => {
        const nodeIssues = validationIssues.filter((issue) => issue.nodeId === node.id);
        const nextValidation = {
          hasError: nodeIssues.some((issue) => issue.type === 'error'),
          hasWarning: nodeIssues.some((issue) => issue.type === 'warning'),
        };
        const currentValidation = (node.data as any)?.validation || {};

        if (
          currentValidation.hasError === nextValidation.hasError &&
          currentValidation.hasWarning === nextValidation.hasWarning
        ) {
          return node;
        }

        changed = true;
        return {
          ...node,
          data: {
            ...node.data,
            validation: nextValidation,
          },
        };
      });
      return changed ? nextNodes : currentNodes;
    });
  }, [setNodes, validationIssues]);

  const canvasRunDisabledReason = useMemo(() => {
    if (!system?.id) return 'Save the AI system before running it';
    if (nodes.length === 0) return 'Add at least one expert before running';
    if (isCanvasRunExecuting) return 'Manual run in progress';
    if (!isCanvasRunConnected) return 'Realtime connection is still connecting';
    if (validationIssues.some((issue) => issue.type === 'error')) {
      return 'Resolve validation errors before running';
    }
    return undefined;
  }, [isCanvasRunConnected, isCanvasRunExecuting, nodes.length, system?.id, validationIssues]);

  const canvasExpertRunStates = useMemo<Record<string, CanvasExpertRunState>>(() => {
    if (!canvasRunExecutionId && !isCanvasRunExecuting && canvasExpertProgress.length === 0) {
      return {};
    }

    const executionId = canvasRunExecutionId ? String(canvasRunExecutionId) : null;
    const nextStates: Record<string, CanvasExpertRunState> = {};

    nodes.forEach((node) => {
      const expert = node.data?.expert as ExpertListItem | undefined;
      const progressEntries = canvasExpertProgress.filter((progress) => String(progress.expert_id) === node.id);
      const loopEntries = progressEntries.filter((progress) => progress.iteration != null);
      const relevantEntries = loopEntries.length > 0 ? loopEntries : progressEntries;
      const hasRunning = relevantEntries.some((progress) => progress.status === 'running' || progress.status === 'pending');
      const hasError = relevantEntries.some((progress) => progress.status === 'error');
      const allSkipped = relevantEntries.length > 0 && relevantEntries.every((progress) => progress.status === 'skipped');
      const hasStopped = relevantEntries.some((progress) => progress.status === 'stopped');
      const hasCompleted = relevantEntries.some((progress) => progress.status === 'completed');
      const status: CanvasExpertRunStatus = relevantEntries.length === 0
        ? 'pending'
        : hasRunning
          ? 'running'
          : hasError
            ? 'error'
            : allSkipped
              ? 'skipped'
              : hasStopped
                ? 'stopped'
                : hasCompleted
                  ? 'completed'
                  : 'pending';
      const totalCost = relevantEntries.reduce((sum, progress) => sum + (progress.total_cost || 0), 0);
      const latestResult = [...relevantEntries].reverse().find((progress) => progress.result)?.result;

      nextStates[node.id] = {
        status: 'pending',
        executionId,
      };

      if (relevantEntries.length > 0) {
        nextStates[node.id] = {
          status,
          executionId,
          totalCost: totalCost || relevantEntries[relevantEntries.length - 1]?.total_cost,
          result: latestResult,
          ...(loopEntries.length > 0 || (expert?.for_loop && Object.keys(expert.for_loop).length > 0)
            ? {
                loopIterationsCompleted: loopEntries.filter((progress) => !['running', 'pending'].includes(progress.status)).length,
                loopIterationsTotal: loopEntries.length || undefined,
              }
            : {}),
        };
      } else if (expert?.for_loop && Object.keys(expert.for_loop).length > 0 && isCanvasRunExecuting) {
        nextStates[node.id] = {
          status: 'pending',
          executionId,
          loopIterationsCompleted: 0,
        };
      }
    });

    return nextStates;
  }, [canvasExpertProgress, canvasRunExecutionId, isCanvasRunExecuting, nodes]);

  const handleCanvasManualRun = useCallback(async () => {
    if (canvasRunDisabledReason) {
      toast.warn(canvasRunDisabledReason);
      return;
    }

    setCurrentExecution(null);
    setExecutionViewerInitialExpertId(null);
    startCanvasExecution({ manual_canvas_run: true, source: 'canvas' }, 'manual');
  }, [canvasRunDisabledReason, startCanvasExecution]);

  const handleCanvasManualStop = useCallback(() => {
    if (!canvasRunExecutionId) {
      toast.info('The run is still starting. Try stopping again once it appears on the canvas.');
      return;
    }

    setIsStoppingCanvasRun(true);
    stopCanvasExecution();
  }, [canvasRunExecutionId, stopCanvasExecution]);

  const handleViewCanvasExpertRun = useCallback(async (expertId: number) => {
    if (!canvasRunExecutionId) {
      toast.info('Execution details will be available after the run starts');
      return;
    }

    try {
      const freshExecution = await executionApi.getSummary(canvasRunExecutionId);
      setCurrentExecution(freshExecution);
      setExecutionViewerInitialExpertId(expertId);
      setExecutionViewerOpen(true);
    } catch (error) {
      console.error('Failed to load canvas execution details:', error);
      toast.error('Failed to load execution details');
    }
  }, [canvasRunExecutionId]);

  const applySystemSnapshotToCanvas = useCallback((nextSystem: AISystem | null) => {
    if (nextSystem) {
      setFormData({
        name: nextSystem.name,
        description: nextSystem.description,
        tags: nextSystem.tags || [],
        is_active: nextSystem.is_active,
        is_template: nextSystem.is_template ?? false,
        is_public: nextSystem.is_public ?? false,
        prevent_concurrent_executions: nextSystem.prevent_concurrent_executions ?? false,
        concurrency_policy: nextSystem.concurrency_policy ?? 'queue',
        config: nextSystem.config || {},
      });

      if (nextSystem.experts && nextSystem.experts.length > 0) {
        const nodesWithIndicatorInfo = nextSystem.experts.map((expert, index) => {
          const savedPosition = nextSystem.config?.expert_positions?.[expert.id] || {
            x: 100 + (index % 3) * 280,
            y: 100 + Math.floor(index / 3) * 180,
          };

          const { hasIndicatorSets, indicatorSetCount } = getIndicatorSetInfo(expert);

          return {
            id: expert.id.toString(),
            type: 'expertNode',
            position: savedPosition,
            data: {
              expert,
              validation: {},
              hasIndicatorSets,
              indicatorSetCount,
            },
          };
        });

        const savedEdges: Edge[] = (nextSystem.config?.expert_connections || []).map((edge: any) => ({
          ...edge,
          id: edge.id || `${edge.source}-${edge.target}`,
          type: 'smoothstep',
          selectable: true,
          deletable: true,
          focusable: true,
          markerEnd: edge.markerEnd || {
            ...createEdgeMarker(DEFAULT_EDGE_COLOR),
          },
          style: edge.style || {
            strokeWidth: 2,
            stroke: DEFAULT_EDGE_COLOR,
          },
        }));
        setNodes(nodesWithIndicatorInfo);
        setEdges(savedEdges);
      } else {
        setNodes([]);
        setEdges([]);
      }
    } else {
      setFormData({
        name: '',
        description: '',
        tags: [],
        is_active: true,
        is_template: false,
        is_public: false,
        prevent_concurrent_executions: false,
        concurrency_policy: 'queue',
        config: {},
      });
      setNodes([]);
      setEdges([]);
    }
    setSelectedNode(null);
  }, [setEdges, setNodes]);

  // Initialize form when editing
  useEffect(() => {
    applySystemSnapshotToCanvas(system || null);
  }, [
    applySystemSnapshotToCanvas,
    system,
    system?.experts?.length,
    system?.config?.expert_ids?.length,
    canvasRefreshTrigger,
  ]); // Re-run when experts count changes or refresh triggered

  // Refetch all data when switching tabs (skip initial mount to avoid reload loop)
  useEffect(() => {
    // Skip on initial mount
    if (isInitialMount) {
      setIsInitialMount(false);
      return;
    }

    if (system && activeTab !== undefined) {
      if (activeTab === 2 || activeTab === 3) {
        queryClient.invalidateQueries({ queryKey: ['experts', system.id] });
      } else if (activeTab === 5) {
        queryClient.invalidateQueries({ queryKey: ['schedules', system.id] });
      } else if (activeTab === 6) {
        queryClient.invalidateQueries({ queryKey: ['executions', system.id] });
      }
    }
  }, [activeTab, system?.id, queryClient]); // Refetch on tab change, but skip initial mount

  // Handle edge connections
  const onConnect = useCallback(
    async (params: Connection) => {
      // Prevent self-connections
      if (params.source === params.target) {
        toast.error('Cannot connect an expert to itself');
        return;
      }
      
      // Add the edge to the canvas with a consistent marker so arrows always render.
      setEdges((eds) => addEdge({
        ...params,
        ...defaultEdgeOptions,
        markerEnd: createEdgeMarker(DEFAULT_EDGE_COLOR),
      }, eds));
      
      // Automatically add source expert as input to target expert
      const sourceNode = nodes.find((n) => n.id === params.source);
      const targetNode = nodes.find((n) => n.id === params.target);
      
      if (sourceNode && targetNode) {
        const sourceExpert = (sourceNode.data as any)?.expert;
        const targetExpert = (targetNode.data as any)?.expert;
        
        if (sourceExpert && targetExpert) {
          try {
            // Fetch the full target expert data to get current input_data
            const fullTargetExpert = await expertApi.get(targetExpert.id);
            
            // Parse input_data
            const currentInputs = fullTargetExpert.input_data?.inputs || [];
            
            // Check if this expert input already exists
            const existingInput = currentInputs.find(
              (input: any) => input.type === 'expert' && input.expert_id === sourceExpert.id
            );
            
            if (!existingInput) {
              // Add new expert input
              const newInput = {
                type: 'expert',
                name: `${sourceExpert.name.toLowerCase().replace(/\s+/g, '_')}_output`,
                description: `Output from ${sourceExpert.name}`,
                expert_id: sourceExpert.id,
                expert_name: sourceExpert.name,
              };
              
              const updatedInputData = {
                inputs: [...currentInputs, newInput],
              };
              
              // Update the expert via API
              await expertApi.update(targetExpert.id, {
                input_data: updatedInputData,
              });
              
              // Update the node data in the canvas to reflect the change
              setNodes((nds) =>
                nds.map((node) =>
                  node.id === params.target
                    ? {
                        ...node,
                        data: {
                          ...node.data,
                          expert: {
                            ...targetExpert,
                            input_data: updatedInputData,
                          },
                        },
                      }
                    : node
                )
              );
              
              toast.success(`Added ${sourceExpert.name} as input to ${targetExpert.name}`);
            }
          } catch (error) {
            console.error('Failed to update expert input_data:', error);
            toast.error('Connection created, but failed to update expert inputs automatically');
          }
        }
      } else {
        toast.success('Connection created');
      }
    },
    [setEdges, nodes, setNodes]
  );

  // Handle node click
  const handleNodeClick = useCallback((_event: React.MouseEvent, node: Node) => {
    setSelectedNodeStartInEditMode(false);
    setSelectedNode(node);
  }, [setSelectedNode, setSelectedNodeStartInEditMode]);

  // Add expert to canvas
  const addExpertToCanvas = useCallback(async (expert: ExpertListItem) => {
    const existingNode = nodes.find((node) => node.id === expert.id.toString());
    if (existingNode) {
      toast.info('Expert already on canvas');
      return;
    }

    const { hasIndicatorSets, indicatorSetCount } = getIndicatorSetInfo(expert);

    const newNode: Node = {
      id: expert.id.toString(),
      type: 'expertNode',
      position: {
        x: Math.random() * 400 + 100,
        y: Math.random() * 300 + 100,
      },
      data: { 
        expert, 
        validation: {}, 
        hasIndicatorSets,
        indicatorSetCount
      },
    };

    setNodes((nds) => [...nds, newNode]);
    toast.success(`Added ${expert.name} to canvas`);
  }, [nodes, setNodes]);

  // Handler for when a new expert is created
  const handleExpertCreated = useCallback(async (createdExpert?: Expert) => {
    await refetchCanvasLibraryExperts();
    if (system?.id) {
      queryClient.invalidateQueries({ queryKey: ['ai-system-validation', system.id] });
    }
    if (createdExpert) {
      addExpertToCanvas(createdExpert as ExpertListItem);
      toast.success(`Expert "${createdExpert.name}" created and added to canvas!`);
    }
    setExpertFormOpen(false);
  }, [addExpertToCanvas, queryClient, refetchCanvasLibraryExperts, system?.id]);

  // Soft delete expert mutation
  const deleteExpertMutation = useMutation({
    mutationFn: (expertId: number) => expertApi.delete(expertId),
    onSuccess: () => {
      // Also remove from canvas if present
      const deletedId = expertToDelete?.id?.toString();
      if (deletedId) {
        setNodes((nds) => nds.filter((node) => node.id !== deletedId));
        setEdges((eds) => eds.filter(
          (edge) => edge.source !== deletedId && edge.target !== deletedId
        ));
      }
      queryClient.invalidateQueries({ queryKey: ['experts'] });
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      if (system) {
        queryClient.invalidateQueries({ queryKey: ['systems', system.id] });
        queryClient.invalidateQueries({ queryKey: ['ai-system-validation', system.id] });
      }
      toast.success(`Expert "${expertToDelete?.name}" deleted successfully`);
      setDeleteConfirmOpen(false);
      setExpertToDelete(null);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.detail || 'Failed to delete expert');
      setDeleteConfirmOpen(false);
      setExpertToDelete(null);
    },
  });

  const handleDeleteExpertClick = useCallback((expert: ExpertListItem) => {
    setExpertToDelete(expert);
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteExpertConfirm = useCallback(() => {
    if (expertToDelete) {
      deleteExpertMutation.mutate(expertToDelete.id);
    }
  }, [expertToDelete, deleteExpertMutation]);

  const handleDeleteExpertCancel = useCallback(() => {
    setDeleteConfirmOpen(false);
    setExpertToDelete(null);
  }, []);

  // Remove expert from canvas
  const removeExpertFromCanvas = useCallback(async (expertId: string) => {
    // Update local state
    const updatedNodes = nodes.filter((node) => node.id !== expertId);
    const updatedEdges = edges.filter(
      (edge) => edge.source !== expertId && edge.target !== expertId
    );
    
    setNodes((nds) => nds.filter((node) => node.id !== expertId));
    setEdges((eds) => eds.filter(
      (edge) => edge.source !== expertId && edge.target !== expertId
    ));

    // Auto-save for existing systems
    if (system) {
      try {
        const expertPositions: Record<string, { x: number; y: number }> = {};
        updatedNodes.forEach((node) => {
          expertPositions[node.id] = node.position;
        });

        const configWithCanvas = {
          ...system.config,
          expert_positions: expertPositions,
          expert_connections: updatedEdges,
          expert_ids: updatedNodes.map((node) => parseInt(node.id)),
        };

        await aiSystemApi.update(system.id, { config: configWithCanvas });
        queryClient.invalidateQueries({ queryKey: ['systems'] });
        queryClient.invalidateQueries({ queryKey: ['systems', system.id] });
        queryClient.invalidateQueries({ queryKey: ['ai-system-validation', system.id] });
        toast.success('Expert removed from canvas');
      } catch (error: any) {
        toast.error('Failed to save canvas changes');
        console.error('Error saving canvas:', error);
      }
    } else {
      toast.success('Expert removed from canvas');
    }
  }, [setNodes, setEdges, nodes, edges, system, queryClient]);

  // Auto-layout function
  const handleAutoLayout = useCallback(() => {
    const layouted = getLayoutedElements(nodes, edges, 'LR');
    setNodes(layouted.nodes);
    setEdges(layouted.edges);
    toast.success('Auto-layout applied');
  }, [nodes, edges, setNodes, setEdges]);

  const handleSaveCanvas = useCallback(async () => {
    if (!system) {
      toast.error('Save the AI system before saving canvas changes');
      return;
    }

    setIsSavingCanvas(true);
    try {
      const expertPositions: Record<string, { x: number; y: number }> = {};
      nodes.forEach((node) => {
        expertPositions[node.id] = node.position;
      });

      const configWithCanvas = {
        ...system.config,
        expert_positions: expertPositions,
        expert_connections: edges,
        expert_ids: nodes.map((node) => parseInt(node.id)),
      };

      await aiSystemApi.update(system.id, { config: configWithCanvas });
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      queryClient.invalidateQueries({ queryKey: ['systems', system.id] });
      queryClient.invalidateQueries({ queryKey: ['ai-system', system.id] });
      queryClient.invalidateQueries({ queryKey: ['ai-system', String(system.id)] });
      queryClient.invalidateQueries({ queryKey: ['ai-system-validation', system.id] });
      toast.success('Canvas changes saved');
    } catch (error: any) {
      console.error('Failed to save canvas changes:', error);
      toast.error(error.response?.data?.detail || 'Failed to save canvas changes');
    } finally {
      setIsSavingCanvas(false);
    }
  }, [edges, nodes, queryClient, system]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: async (data: AISystemCreate) => {
      const expertPositions: Record<string, { x: number; y: number }> = {};
      nodes.forEach((node) => {
        expertPositions[node.id] = node.position;
      });

      const configWithCanvas = {
        ...data.config,
        expert_positions: expertPositions,
        expert_connections: edges,
        expert_ids: nodes.map((node) => parseInt(node.id)),
      };

      return aiSystemApi.create({ ...data, config: configWithCanvas });
    },
    onSuccess: (createdSystem) => {
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      toast.success('AI System created successfully');
      
      // Navigate immediately - this will change the route and unmount AISystemList
      navigate(`/ai/systems/${createdSystem.id}`, { replace: true });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to create system');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: async (data: AISystemCreate) => {
      if (!system) return;

      const expertPositions: Record<string, { x: number; y: number }> = {};
      nodes.forEach((node) => {
        expertPositions[node.id] = node.position;
      });

      const configWithCanvas = {
        ...data.config,
        expert_positions: expertPositions,
        expert_connections: edges,
        expert_ids: nodes.map((node) => parseInt(node.id)),
      };

      return aiSystemApi.update(system.id, { ...data, config: configWithCanvas });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systems'] });
      if (system?.id) {
        queryClient.invalidateQueries({ queryKey: ['ai-system-validation', system.id] });
      }
      toast.success('AI System updated successfully');
      setSettingsOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update system');
    },
  });

  const handleSubmit = () => {
    // Validation
    if (!formData.name.trim()) {
      toast.error('System name is required');
      return;
    }

    if (nodes.length === 0) {
      toast.warning('System has no experts');
    }
    
    const errors = validationIssues.filter(issue => issue.type === 'error');
    if (errors.length > 0) {
      toast.error(`Cannot save: ${errors.length} validation error(s)`);
      return;
    }

    if (system) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  // Handler for viewing execution details from history
  const handleViewExecution = async (execution: AISystemExecution | AISystemExecutionListItem) => {
    try {
      // Fetch fresh lightweight data; heavy expert payloads load on demand.
      const freshExecution = await executionApi.getSummary(execution.id);
      setCurrentExecution(freshExecution);
      setExecutionViewerInitialExpertId(null);
      setExecutionViewerOpen(true);
    } catch (error: any) {
      console.error('Error fetching execution details:', error);
      toast.error('Failed to load execution details');
    }
  };

  const handleManualRun = async () => {
    if (!system) {
      toast.error('Please save the system first before running it manually');
      return;
    }
    if (!systemManualRunAccess.ensureAccess()) return;

    if (nodes.length === 0) {
      toast.warning('System has no experts to execute');
      return;
    }

    setManualRunRunning(true);
    
    // Use a flag to track if we've already reset the state
    let hasReset = false;
    
    // Safety timeout to ensure button never gets stuck
    const safetyTimeout = setTimeout(() => {
      if (!hasReset) {
        console.warn('Safety timeout triggered - resetting manual run button state');
        setManualRunRunning(false);
        hasReset = true;
      }
    }, 65000); // 65 seconds safety net
    
    try {
      const execution = await aiSystemApi.execute(system.id, {
        execution_type: 'manual',
      });
      
      toast.success('Manual run started');

      // Poll for completion
      const pollExecution = async () => {
        let retries = 0;
        const maxRetries = 60; // 60 seconds max
        
        const checkStatus = async (): Promise<void> => {
          if (retries >= maxRetries) {
            toast.warning('Execution is taking longer than expected. Switch to Activity to check status.');
            if (!hasReset) {
              setManualRunRunning(false);
              hasReset = true;
            }
            setActivityTab(ACTIVITY_TABS.RUNS);
            setActiveTab(SYSTEM_TABS.ACTIVITY);
            return;
          }

          try {
            const updatedExecution = await executionApi.getSummary(execution.id);
            
            // Terminal statuses that should stop polling
            const terminalStatuses = ['success', 'completed', 'failed', 'error', 'cancelled', 'canceled'];
            const normalizedStatus = (updatedExecution.status || '').toLowerCase();

            if (terminalStatuses.includes(normalizedStatus)) {
              setCurrentExecution(updatedExecution);
              setExecutionViewerInitialExpertId(null);
              setExecutionViewerOpen(true);
              
              if (!hasReset) {
                setManualRunRunning(false);
                hasReset = true;
              }
              
              if (normalizedStatus === 'success' || normalizedStatus === 'completed') {
                toast.success('Execution completed successfully');
              } else if (normalizedStatus === 'failed' || normalizedStatus === 'error') {
                toast.error('Execution failed');
              } else {
                toast.info(`Execution ${normalizedStatus}`);
              }
            } else {
              // Still running, poll again
              retries++;
              setTimeout(checkStatus, 1000);
            }
          } catch (error) {
            console.error('Error polling execution:', error);
            if (!hasReset) {
              setManualRunRunning(false);
              hasReset = true;
            }
            toast.error('Error checking execution status');
          }
        };

        await checkStatus();
      };

      await pollExecution();
    } catch (error: any) {
      console.error('Manual run error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start manual run');
      if (!hasReset) {
        setManualRunRunning(false);
        hasReset = true;
      }
    } finally {
      // Clear the safety timeout
      clearTimeout(safetyTimeout);
      
      // Final safety check - if for some reason state wasn't reset, do it now
      if (!hasReset) {
        setManualRunRunning(false);
      }
    }
  };

  const validationErrorCount = validationIssues.filter((issue) => issue.type === 'error').length;
  const validationWarningCount = validationIssues.filter((issue) => issue.type === 'warning').length;
  const validationInfoCount = validationIssues.filter((issue) => issue.type === 'info').length;
  const validationHasError = validationErrorCount > 0;
  const validationHasWarning = validationWarningCount > 0;
  const actionableIssueCount = validationErrorCount + validationWarningCount;
  const issueCount = validationIssues.length;
  const workflowReady = !!system && nodes.length > 0 && !validationHasError;
  const checklistItems = [
    {
      label: 'Name your system',
      done: !!formData.name?.trim(),
    },
    {
      label: 'Create the system record',
      done: !!system,
    },
    {
      label: 'Add at least one expert',
      done: !!system && nodes.length > 0,
    },
    {
      label: 'Connect the workflow',
      done: !!system && (nodes.length < 2 || edges.length > 0),
    },
    {
      label: 'Resolve validation issues',
      done: actionableIssueCount === 0,
    },
  ];

  const completedChecklistCount = checklistItems.filter((item) => item.done).length;

  const nextRecommendedStep = useMemo(() => {
    if (!formData.name?.trim()) return 'Name the system to start building.';
    if (!system) return 'Create the system to unlock the builder, schedules, and activity views.';
    if (nodes.length === 0) return 'Add your first expert to the canvas.';
    if (nodes.length > 1 && edges.length === 0) return 'Connect your experts to define the workflow.';
    if (validationHasError) return 'Fix the validation errors before running a test.';
    return 'Run a test execution to verify the workflow.';
  }, [edges.length, formData.name, nodes.length, system, validationHasError]);

  const renderValidationReport = () => (
    <Box p={3}>
      <Typography variant="h6" gutterBottom>
        Workflow Validation
      </Typography>

      {validationIssues.length === 0 ? (
        <Alert severity="success" icon={<ValidIcon />}>
          <AlertTitle>All checks passed</AlertTitle>
          Your workflow is valid and ready to run.
        </Alert>
      ) : (
        <Stack spacing={2}>
          <Alert severity={validationHasError ? 'error' : validationHasWarning ? 'warning' : 'info'}>
            <AlertTitle>
              {validationErrorCount} error(s), {validationWarningCount} warning(s), {validationInfoCount} info
            </AlertTitle>
            {validationHasError
              ? 'Resolve all errors before saving or running the system.'
              : validationHasWarning
                ? 'Review the warnings to tighten the workflow.'
                : 'Review the informational validation notes.'}
          </Alert>

          {validationIssues.map((issue, idx) => (
            <Alert key={idx} severity={issue.type}>
              {issue.message}
            </Alert>
          ))}
        </Stack>
      )}
    </Box>
  );

  const renderAIBuilderAssistant = () => {
    if (!system) {
      return (
        <Box p={3}>
          <Alert severity="info">Create the system first, then open the AI assistant to help build it.</Alert>
        </Box>
      );
    }

    if (agentLoading || selectedAgentLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading AI assistant...</Typography>
        </Box>
      );
    }

    if (agentError && !systemBuilderAgents.length) {
      return (
        <Box p={3}>
          <Alert severity="error">No AI System Builder agents found. Create one from the Agents page first.</Alert>
        </Box>
      );
    }

    if (!managerAgent) {
      return (
        <Box p={3}>
          <Alert severity="info">Select an agent to start using the AI assistant.</Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ flex: 1, minHeight: 0, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <AIBuilder
          system={system}
          agent={managerAgent}
          headerActions={
            <>
              <Tooltip title="Open active AI Manager agent">
                <span>
                  <IconButton
                    size="small"
                    aria-label="Open active AI Manager agent"
                    onClick={() =>
                      selectedAgentId &&
                      window.open(
                        `/ai/agents/${encodeURIComponent(String(selectedAgentId))}`,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                    disabled={!selectedAgentId}
                    color="primary"
                    sx={{ flexShrink: 0 }}
                  >
                    <ViewIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <AgentAutocomplete
                value={selectedAgentId}
                onChange={(agentId) => setSelectedAgentId(agentId)}
                label=""
                filters={{ is_active: true, agent_type: 'AI_SYSTEM_BUILDER' }}
                sx={{
                  minWidth: { sm: 180, md: 240 },
                  maxWidth: 340,
                  flex: '1 1 240px',
                  display: { xs: 'none', sm: 'block' },
                  '& .MuiInputBase-root': { height: 32 },
                  '& .MuiInputBase-input': { py: 0.25, fontSize: '0.82rem' },
                }}
              />
            </>
          }
          onExpertCreated={async () => {
            if (!system?.id) return;
            queryClient.invalidateQueries({ queryKey: ['ai-system', system.id] });
            queryClient.invalidateQueries({ queryKey: ['ai-system', String(system.id)] });
            queryClient.invalidateQueries({ queryKey: ['experts', system.id] });
            queryClient.invalidateQueries({ queryKey: ['schedules', system.id] });
            queryClient.invalidateQueries({ queryKey: ['executions', system.id] });
            const refreshedSystem = await aiSystemApi.get(system.id);
            queryClient.setQueryData(['ai-system', system.id], refreshedSystem);
            queryClient.setQueryData(['ai-system', String(system.id)], refreshedSystem);
            applySystemSnapshotToCanvas(refreshedSystem);
            await queryClient.refetchQueries({ queryKey: ['experts', system.id] });
            await refetchCanvasLibraryExperts();
          }}
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={handleNodeClick}
          onAutoLayout={handleAutoLayout}
          expertsData={canvasExpertsData}
          addExpertToCanvas={addExpertToCanvas}
          setExpertFormOpen={setExpertFormOpen}
          selectedNode={selectedNode}
          setSelectedNode={setSelectedNode}
          setNodes={setNodes}
          setEdges={setEdges}
          refetchExperts={refetchCanvasLibraryExperts}
          validationIssues={validationIssues}
          onSaveCanvas={handleSaveCanvas}
          isSavingCanvas={isSavingCanvas}
          onRunCanvas={handleCanvasManualRun}
          onStopCanvas={handleCanvasManualStop}
          isRunningCanvas={isCanvasRunExecuting}
          isStoppingCanvas={isStoppingCanvasRun}
          canRunCanvas={!canvasRunDisabledReason}
          runDisabledReason={canvasRunDisabledReason}
          expertRunStates={canvasExpertRunStates}
          onViewExpertRun={handleViewCanvasExpertRun}
          nodeTypes={nodeTypes}
          onExpertUpdate={async (updatedExpert) => {
            if (updatedExpert === null) {
              setNodes((nds) => nds.filter((node) => node.id !== selectedNode?.id));
              setEdges((eds) => eds.filter((edge) => edge.source !== selectedNode?.id && edge.target !== selectedNode?.id));
              setSelectedNode(null);
            } else {
              setNodes((nds) =>
                nds.map((node) => {
                  if (node.id === selectedNode?.id) {
                    return {
                      ...node,
                      data: {
                        ...node.data,
                        expert: updatedExpert,
                      },
                    };
                  }
                  return node;
                })
              );
            }
            refetchExperts();
          }}
        />
      </Box>
    );
  };

  // Content component that can be used in both dialog and page mode
  const renderContent = () => (
    <>
      <Box sx={{ 
        p: 0, 
        flex: 1,
        minHeight: 0,
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden' 
      }}>
        <Box sx={{ 
          position: 'sticky',
          top: 0,
          backgroundColor: 'background.paper',
          zIndex: 10,
          flexShrink: 0,
          borderBottom: 1,
          borderColor: 'divider',
        }}>
          {/* Single combined header + tabs strip */}
          <Box sx={{
            px: { xs: 1, sm: 1.5 },
            py: { xs: 0.5, sm: 0.75 },
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 2 },
            minHeight: { xs: 52, sm: 60 },
            flexWrap: 'nowrap',
            overflow: 'hidden',
          }}>
            {/* Left: name */}
            <Box
              display="flex"
              alignItems="center"
              gap={1.5}
              sx={{
                display: { xs: 'none', md: 'flex' },
                flex: { xs: '0 1 auto', md: 1 },
                pr: { xs: 0, sm: 1 },
                minWidth: 0,
                maxWidth: { xs: 112, sm: 260, md: 'none' },
              }}
            >
              {asPage && !isMobile && (
                <PageBackButton
                  label="Back to Systems"
                  to="/ai/systems"
                  onClick={handleCloseRequest}
                />
              )}
              <Typography
                variant="subtitle1"
                noWrap
                sx={{
                  fontWeight: 600,
                  fontSize: { xs: '0.9rem', sm: '0.95rem' },
                  minWidth: 0,
                }}
              >
                {formData.name || 'Untitled System'}
              </Typography>
              {formData.is_template && (
                <Chip
                  label="Template"
                  size="small"
                  color="secondary"
                  variant="outlined"
                />
              )}
              {formData.is_template && formData.is_public && (
                <Chip
                  label="Public"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              )}
            </Box>

            {/* Center: Tabs */}
            <Box sx={{
              flex: { xs: '1 1 0%', md: '0 1 auto' },
              minWidth: 0,
              maxWidth: '100%',
              overflow: 'hidden',
            }}>
              <Tabs
                value={activeTab}
                onChange={(_, newValue) => setActiveTab(newValue)}
                variant="scrollable"
                scrollButtons={false}
                TabIndicatorProps={{ style: { display: 'none' } }}
                sx={{
                  ...scrollableTabsSx,
                  width: { xs: '100%', md: 'max-content' },
                  minHeight: { xs: 40, sm: 44 },
                  '& .MuiTab-root': {
                    flexShrink: 0,
                    textTransform: 'none',
                    alignItems: 'center',
                    borderBottom: '3px solid transparent',
                    minWidth: { xs: 64, sm: 'auto' },
                    minHeight: { xs: 40, sm: 44 },
                    px: { xs: 1, sm: 1.5 },
                    fontSize: { xs: '0.75rem', sm: '0.85rem' },
                    '&.Mui-selected': {
                      borderBottom: '3px solid',
                      borderColor: 'primary.main',
                    },
                  },
                }}
              >
                <Tab
                  value={SYSTEM_TABS.BUILDER}
                  label="Builder"
                  icon={nodes.length > 0 ? <Chip label={nodes.length} size="small" color="primary" sx={{ height: 18, fontSize: '0.7rem' }} /> : undefined}
                  iconPosition="end"
                />
                {system && <Tab value={SYSTEM_TABS.RUN_AUTOMATE} label="Run & Automate" />}
                {system && <Tab value={SYSTEM_TABS.ACTIVITY} label="Activity" />}
                {system && <Tab value={SYSTEM_TABS.ADVANCED} label="Advanced" />}
              </Tabs>
            </Box>

            {/* Right: actions */}
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flex: { xs: '0 0 auto', md: 1 }, justifyContent: 'flex-end', minWidth: 0 }}>
              {!system && (
                <Button
                  size="small"
                  variant="contained"
                  onClick={handleSubmit}
                  disabled={createMutation.isPending || updateMutation.isPending || validationHasError}
                  startIcon={<SaveIcon />}
                  sx={{ minWidth: 0 }}
                >
                  {isMobile ? '' : system ? 'Save' : 'Create'}
                </Button>
              )}
              {actionableIssueCount > 0 && (
                <Tooltip title={nextRecommendedStep}>
                  <IconButton
                    size="small"
                    aria-label={workflowNoticeExpanded ? 'Collapse workflow warning' : 'Expand workflow warning'}
                    onClick={() => setWorkflowNoticeExpanded((expanded) => !expanded)}
                    sx={{
                      color: validationHasError ? 'error.main' : 'warning.main',
                      bgcolor: workflowNoticeExpanded ? 'action.selected' : 'transparent',
                    }}
                  >
                    <WarningIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              <IconButton
                size="small"
                onClick={() => setSettingsOpen(true)}
                title="System settings"
              >
                <SettingsIcon fontSize="small" />
              </IconButton>
              {!asPage && (
                <IconButton
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  size="small"
                  sx={{ display: { xs: 'none', sm: 'flex' } }}
                >
                  {isFullscreen ? <FullscreenExitIcon /> : <FullscreenIcon />}
                </IconButton>
              )}
              {!asPage && (
                <IconButton onClick={handleCloseRequest} size="small">
                  <CloseIcon />
                </IconButton>
              )}
            </Stack>
          </Box>

          {actionableIssueCount > 0 && workflowNoticeExpanded && (
            <Box
              sx={{
                position: 'absolute',
                top: 'calc(100% + 8px)',
                right: { xs: 8, sm: 12 },
                left: { xs: 8, sm: 'auto' },
                zIndex: 20,
              }}
            >
              <Alert
                severity={validationHasError ? 'error' : 'warning'}
                action={
                  <IconButton
                    size="small"
                    aria-label="Collapse workflow warning"
                    onClick={() => setWorkflowNoticeExpanded(false)}
                  >
                    <ExpandMoreIcon fontSize="small" />
                  </IconButton>
                }
                sx={{
                  width: { xs: '100%', sm: 460, md: 560 },
                  maxWidth: { xs: 'calc(100vw - 16px)', md: 'calc(100vw - var(--sidebar-offset, 0px) - 24px)' },
                  border: 1,
                  borderColor: validationHasError ? 'error.dark' : 'warning.dark',
                  boxShadow: 6,
                  '& .MuiAlert-message': {
                    minWidth: 0,
                    width: '100%',
                  },
                  '& .MuiAlert-action': {
                    alignItems: 'flex-start',
                    pl: 1,
                  },
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.4 }}>
                    {nextRecommendedStep}
                  </Typography>
                  <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                    {validationIssues.slice(0, 3).map((issue, idx) => (
                      <Typography key={`${issue.type}-${idx}`} variant="caption" color="text.secondary">
                        {issue.message}
                      </Typography>
                    ))}
                    {validationIssues.length > 3 && (
                      <Typography variant="caption" color="text.secondary">
                        {validationIssues.length - 3} more issue(s) in Validation.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              </Alert>
            </Box>
          )}

          {/* Settings Dialog */}
          <Dialog
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            slotProps={{ paper: { sx: { minHeight: { xs: '100%', sm: 560 } } } }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              System Settings
            </DialogTitle>

            <DialogContent dividers>
            <Stack spacing={2.5}>
              <TextField
                label="Name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                fullWidth
                size="small"
                error={!formData.name}
                helperText={!formData.name ? 'Required' : ''}
              />
              <TextField
                label="Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                minRows={8}
                maxRows={16}
                fullWidth
                placeholder="Describe the purpose, scope, and operating notes for this workflow."
                helperText="Use this for the workflow brief, assumptions, constraints, and handoff notes."
                sx={{
                  '& textarea': {
                    resize: 'vertical',
                  },
                }}
              />
              <Autocomplete<string, true, false, true>
                multiple
                freeSolo
                options={[]}
                value={formData.tags || []}
                onChange={(_, value) => setFormData({ ...formData, tags: normalizeTags(value) })}
                renderTags={(value, getTagProps) =>
                  value.map((tag, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={tag}
                      label={tag}
                      size="small"
                      variant="outlined"
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Tags"
                    size="small"
                    placeholder="Add tag"
                    helperText="Tags improve template search by market, strategy, timeframe, or workflow."
                  />
                )}
              />
              <Divider />
              <FormControlLabel
                sx={{ m: 0, justifyContent: 'space-between' }}
                control={
                  <Switch
                    size="small"
                    checked={formData.is_active}
                    onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  />
                }
                label={<Typography variant="body2">Active</Typography>}
                labelPlacement="start"
              />
              <FormControlLabel
                sx={{ m: 0, justifyContent: 'space-between' }}
                control={
                  <Switch
                    size="small"
                    checked={formData.is_template ?? false}
                    onChange={(e) => {
                      const isTemplate = e.target.checked;
                      setFormData({
                        ...formData,
                        is_template: isTemplate,
                        is_public: isTemplate ? formData.is_public : false,
                      });
                    }}
                  />
                }
                label={<Typography variant="body2">Template</Typography>}
                labelPlacement="start"
              />
              <FormControlLabel
                sx={{ m: 0, justifyContent: 'space-between' }}
                control={
                  <Switch
                    size="small"
                    checked={Boolean(formData.is_template && formData.is_public)}
                    onChange={(e) => setFormData({ ...formData, is_public: e.target.checked })}
                    disabled={!formData.is_template}
                  />
                }
                label={<Typography variant="body2">Public</Typography>}
                labelPlacement="start"
              />
              <FormControlLabel
                sx={{ m: 0, justifyContent: 'space-between' }}
                control={
                  <Switch
                    size="small"
                    checked={formData.config?.require_approval ?? true}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        config: { ...formData.config, require_approval: e.target.checked },
                      })
                    }
                  />
                }
                label={<Typography variant="body2">Trade Approval</Typography>}
                labelPlacement="start"
              />
              <Divider />
              <FormControlLabel
                sx={{ m: 0, justifyContent: 'space-between' }}
                control={
                  <Switch
                    size="small"
                    checked={formData.prevent_concurrent_executions ?? false}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        prevent_concurrent_executions: e.target.checked,
                      })
                    }
                  />
                }
                label={<Typography variant="body2">Prevent Concurrent Executions</Typography>}
                labelPlacement="start"
              />
              <FormControl fullWidth size="small" disabled={!formData.prevent_concurrent_executions}>
                <InputLabel id="system-concurrency-policy-label">Concurrency Policy</InputLabel>
                <Select
                  labelId="system-concurrency-policy-label"
                  label="Concurrency Policy"
                  value={formData.concurrency_policy ?? 'queue'}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      concurrency_policy: e.target.value as 'queue' | 'drop',
                    })
                  }
                >
                  <MenuItem value="queue">Queue</MenuItem>
                  <MenuItem value="drop">Drop</MenuItem>
                </Select>
              </FormControl>
            </Stack>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={() => setSettingsOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={createMutation.isPending || updateMutation.isPending || validationHasError || !formData.name?.trim()}
                startIcon={createMutation.isPending || updateMutation.isPending ? <CircularProgress size={14} /> : <SaveIcon />}
              >
                {createMutation.isPending || updateMutation.isPending ? 'Saving...' : system ? 'Save Settings' : 'Create System'}
              </Button>
            </DialogActions>
          </Dialog>

        </Box>

        <TabPanel value={activeTab} index={SYSTEM_TABS.BUILDER}>
          <Box p={0} display="flex" flexDirection="column" gap={0} height="100%" minHeight={0}>
            {system ? (
              <Box flex={1} minHeight={0} display="flex" flexDirection="column">
                {renderAIBuilderAssistant()}
              </Box>
            ) : (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="h6" gutterBottom>
                  Create the system to unlock the builder
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Once the system is created, you can add experts, connect the workflow on the canvas, and use the AI assistant to help build it.
                </Typography>
              </Paper>
            )}
          </Box>
        </TabPanel>

        {system && (
          <TabPanel value={activeTab} index={SYSTEM_TABS.RUN_AUTOMATE}>
            <Box p={{ xs: 1.5, sm: 2 }} display="flex" flexDirection="column" gap={2} height="100%" minHeight={0}>
              <Tabs
                value={runAutomateTab}
                onChange={(_, newValue) => setRunAutomateTab(newValue)}
                variant="scrollable"
                scrollButtons={false}
                sx={{ ...scrollableTabsSx, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab value={RUN_AUTOMATE_TABS.SCHEDULES} label="Schedules" />
                <Tab value={RUN_AUTOMATE_TABS.MANUAL_RUN} label="Manual Run" />
                {isAdmin && <Tab value={RUN_AUTOMATE_TABS.BACKTESTING} label="Backtesting" />}
              </Tabs>

              {runAutomateTab === RUN_AUTOMATE_TABS.MANUAL_RUN && (
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, lg: 8 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Manual Run
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Run the AI system manually before scheduling it or relying on automated trade actions.
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Button
                            variant="contained"
                            onClick={handleManualRun}
                            disabled={manualRunRunning || validationHasError || systemManualRunAccess.disabled}
                            startIcon={manualRunRunning ? <CircularProgress size={14} /> : <PlayArrowIcon />}
                            title={systemManualRunAccess.disabled ? systemManualRunAccess.blockedMessage : undefined}
                          >
                            {manualRunRunning ? 'Running...' : 'Run Manual'}
                          </Button>
                          {currentExecution && (
                            <Button variant="outlined" onClick={() => setExecutionViewerOpen(true)}>
                              Open Latest Result
                            </Button>
                          )}
                        </Stack>
                        {validationHasError && (
                          <Alert severity="warning" sx={{ mt: 2 }}>
                            Resolve validation errors before running a production-quality test.
                          </Alert>
                        )}
                        {canvasExpertProgress.length > 0 && (
                          <Box sx={{ mt: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              Live expert progress
                            </Typography>
                            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                              {canvasExpertProgress.map((expert, index) => (
                                <Chip
                                  key={`${expert.expert_id}-${expert.iteration ?? 'main'}-${index}`}
                                  label={expert.iteration ? `${expert.expert_name} #${expert.iteration}` : expert.expert_name}
                                  size="small"
                                  color={
                                    expert.status === 'completed' ? 'success' :
                                    expert.status === 'running' ? 'primary' :
                                    expert.status === 'error' ? 'error' :
                                    expert.status === 'skipped' ? 'warning' : 'default'
                                  }
                                  variant={expert.status === 'running' ? 'filled' : 'outlined'}
                                />
                              ))}
                            </Stack>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid size={{ xs: 12, lg: 4 }}>
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom>
                          Workflow Snapshot
                        </Typography>
                        <Stack spacing={1}>
                          <Typography variant="body2" color="text.secondary">Experts: <strong>{nodes.length}</strong></Typography>
                          <Typography variant="body2" color="text.secondary">Connections: <strong>{edges.length}</strong></Typography>
                          <Typography variant="body2" color="text.secondary">Issues: <strong>{actionableIssueCount}</strong></Typography>
                          <Typography variant="body2" color="text.secondary">Status: <strong>{workflowReady ? 'Ready to run' : 'Needs setup'}</strong></Typography>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {runAutomateTab === RUN_AUTOMATE_TABS.SCHEDULES && (
                <SchedulePanel systemId={system.id} />
              )}

              {isAdmin && runAutomateTab === RUN_AUTOMATE_TABS.BACKTESTING && (
                <BacktestPanel systemId={system.id} />
              )}
            </Box>
          </TabPanel>
        )}

        {system && (
          <TabPanel value={activeTab} index={SYSTEM_TABS.ACTIVITY}>
            <Box p={{ xs: 1.5, sm: 2 }} display="flex" flexDirection="column" gap={2} height="100%" minHeight={0}>
              <Tabs
                value={activityTab}
                onChange={(_, newValue) => setActivityTab(newValue)}
                variant="scrollable"
                scrollButtons={false}
                sx={{ ...scrollableTabsSx, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab value={ACTIVITY_TABS.RUNS} label="Runs" />
                <Tab value={ACTIVITY_TABS.TRADES} label="Trades" />
                <Tab value={ACTIVITY_TABS.INSIGHTS} label="Insights" />
              </Tabs>

              {activityTab === ACTIVITY_TABS.RUNS && (
                <ExecutionHistoryPanel
                  systemId={system.id}
                  onViewExecution={handleViewExecution}
                  enableAutoRefresh={activeTab === SYSTEM_TABS.ACTIVITY && activityTab === ACTIVITY_TABS.RUNS}
                />
              )}
              {activityTab === ACTIVITY_TABS.TRADES && <TradesPanel systemId={system.id} />}
              {activityTab === ACTIVITY_TABS.INSIGHTS && <AnalyticsPanel systemId={system.id} />}
            </Box>
          </TabPanel>
        )}

        {system && (
          <TabPanel value={activeTab} index={SYSTEM_TABS.ADVANCED}>
            <Box p={{ xs: 1.5, sm: 2 }} display="flex" flexDirection="column" gap={2} height="100%" minHeight={0}>
              <Tabs
                value={advancedTab}
                onChange={(_, newValue) => setAdvancedTab(newValue)}
                variant="scrollable"
                scrollButtons={false}
                sx={{ ...scrollableTabsSx, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab value={ADVANCED_TABS.SHARED_DATA} label="Shared Data" />
                <Tab value={ADVANCED_TABS.STORAGE} label="Storage" />
                <Tab
                  value={ADVANCED_TABS.VALIDATION}
                  label="Validation"
                  icon={issueCount > 0 ? <Chip label={issueCount} size="small" color={validationHasError ? 'error' : validationHasWarning ? 'warning' : 'info'} sx={{ height: 20, fontSize: '0.75rem' }} /> : undefined}
                  iconPosition="end"
                />
                <Tab value={ADVANCED_TABS.HELP} label="Help" />
              </Tabs>

              {advancedTab === ADVANCED_TABS.SHARED_DATA && <SharedInputsPanel systemId={system.id} />}
              {advancedTab === ADVANCED_TABS.STORAGE && <StoragePanel systemId={system.id} />}
              {advancedTab === ADVANCED_TABS.VALIDATION && renderValidationReport()}
              {advancedTab === ADVANCED_TABS.HELP && <HelpDocumentationPanel />}
            </Box>
          </TabPanel>
        )}

      </Box>

      {/* Nested Expert Form */}
      <ExpertForm
        open={expertFormOpen}
        onClose={() => {
          setExpertFormOpen(false);
        }}
        onSuccess={handleExpertCreated}
        aiSystemId={system?.id}
      />

      {/* Execution Viewer Dialog */}
      <ExecutionViewerDialog
        open={executionViewerOpen}
        onClose={() => {
          setExecutionViewerOpen(false);
          setExecutionViewerInitialExpertId(null);
        }}
        execution={currentExecution}
        initialExpertId={executionViewerInitialExpertId}
      />

    </>
  );

  // Render as page or dialog based on asPage prop
  if (asPage) {
    return (
      <Box sx={{ 
        // Use fixed positioning to fill viewport space alongside global sidebar
        position: 'fixed',
        top: 'var(--topbar-height, 44px)',
        left: 'var(--sidebar-offset, 0px)',
        right: 0,
        bottom: 0,
        display: 'flex', 
        flexDirection: 'column', 
        overflow: 'hidden',
        bgcolor: 'background.default',
        zIndex: 1,
        transition: 'left 0.3s ease',
      }}>
        {renderContent()}
      </Box>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={handleCloseRequest} 
      maxWidth={isFullscreen ? false : "xl"} 
      fullWidth 
      fullScreen={isFullscreen || isMobile}
      sx={{
        '& .MuiDialog-paper': {
          margin: isMobile ? 0 : undefined,
          maxHeight: isMobile ? '100%' : undefined,
          maxWidth: isMobile ? '100%' : undefined,
          borderRadius: isMobile ? 0 : undefined,
        },
        '& .MuiDialog-container': {
          alignItems: isMobile ? 'stretch' : undefined,
        },
      }}
    >
      {renderContent()}
    </Dialog>
  );
};
